import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import AdminShell from '../Layout/AdminShell';
import {
  PageHead, Panel, Pill, Select, Modal, ConfirmModal, Table, Row, Cell, Avatar, btn, field,
} from '../common/AdminUI';
import ShipmentFilters from '../common/ShipmentFilters';
import { matchesShipmentSearch } from '../../utils/shipmentSearch';
import ShipmentsTable from '../common/ShipmentsTable';
import SeaExportTableReadOnly from './SeaExportTableReadOnly';
import InvoiceGenerationPage from './InvoiceGenerationPage';
import UpdatePaymentModal from '../common/UpdatePaymentModal';
import { mockUsers } from '../../utils/mockData';
import { API_BASE_URL, API_ENDPOINTS, apiCall } from '../../config/api';
import { X, FileText, Upload, FileDown, Plane, Ship, Download, AlertTriangle, Edit, ArrowUpRight, ArrowDownLeft, Eye, Pencil, Trash2, Calendar, Hash, Users, Receipt, Search, ChevronDown, RotateCcw, FileSpreadsheet } from 'lucide-react';
import { uploadAccountantShipmentPDFs, uploadKYCDocuments, getCloudFrontUrl } from '../../utils/s3Upload';
import { exportShipmentsExcel } from '../../utils/exportShipmentsExcel';
import HsnCodeManager from '../common/HsnCodeManager';
import ClientMaster from '../common/ClientMaster';
import BankStatement from '../Accounts/BankStatement';
import { listCreditors, extractPur, listPurDocuments } from '../Accounts/reconApi';
import PurViewerModal from '../Accounts/PurViewerModal';
import { CustomerInvoicesModal } from '../common/CustomerInvoices';
import { useNotifications } from '../../context/NotificationContext';
import { DateRangePicker } from 'react-date-range';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import {
  DATE_PRESETS, PaymentStatusFilter, UnpaidSinceFilter, OverdueDaysFilter, makePaymentMatcher,
} from '../common/InvoiceFilters';

const AccountantDashboard = () => {
  const [activeTab, setActiveTab] = useState('Air-Export');
  const [hsnSearch, setHsnSearch] = useState('');
  const [clientSearch, setClientSearch] = useState('');
  const [kycSearch, setKycSearch] = useState('');
  const [bankSearch, setBankSearch] = useState('');
  const { highlightedShipment, setHighlightedShipment } = useNotifications();

  // Auto-switch tab when a notification highlight is set
  useEffect(() => {
    if (highlightedShipment?.type) {
      setActiveTab(highlightedShipment.type);
      // Auto-clear highlight after 5 seconds
      const timer = setTimeout(() => setHighlightedShipment(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [highlightedShipment, setHighlightedShipment]);
  const [selectedCustomer, setSelectedCustomer] = useState('');
  const [customers, setCustomers] = useState([]);
  const [shipments, setShipments] = useState([]);
  const [seaExportShipments, setSeaExportShipments] = useState([]);
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [shipmentsLoading, setShipmentsLoading] = useState(false);
  const [loadingSeaExport, setLoadingSeaExport] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedShipment, setSelectedShipment] = useState(null);
  // A PUR is a bill from a creditor, so the upload now says whose bill it is.
  // The value is held here ready for the API that will store it; until that
  // exists the upload itself goes out exactly as before.
  const [creditorList, setCreditorList] = useState([]);
  // Every purchase bill, keyed by the shipment it was filed against, so the
  // table can show it without asking per row.
  const [purMap, setPurMap] = useState({});
  const [purViewing, setPurViewing] = useState(null);
  const [editCreditorId, setEditCreditorId] = useState('');
  const [seaEditCreditorId, setSeaEditCreditorId] = useState('');

  useEffect(() => {
    let alive = true;
    listCreditors().then((l) => { if (alive) setCreditorList(l); }).catch(() => {});
    return () => { alive = false; };
  }, []);

  const loadPurMap = React.useCallback(async () => {
    try {
      const docs = await listPurDocuments();
      const bare = (v) => String(v || '').replace(/[^0-9a-z]/gi, '');
      const next = {};
      // Newest first, so the first bill seen for an AWB is the one that counts.
      docs.forEach((d) => {
        const k = bare(d.awbNumber);
        if (k && !next[k]) next[k] = d;
      });
      setPurMap(next);
    } catch { /* the table simply shows no bill */ }
  }, []);
  useEffect(() => { loadPurMap(); }, [loadPurMap]);

  const [editFiles, setEditFiles] = useState({
    invoice: null,
    pur: null
  });
  const [showSeaExportEditModal, setShowSeaExportEditModal] = useState(false);
  const [selectedSeaShipment, setSelectedSeaShipment] = useState(null);
  const [seaEditFiles, setSeaEditFiles] = useState({
    invoice: null,
    pur: null
  });
  const [kycFiles, setKycFiles] = useState({
    kyc: null,
    promiseLetter: null,
    bankLetter: null,
    serviceAggre: null
  });
  const [existingKycDocs, setExistingKycDocs] = useState({
    kyc: null,
    promiseLetter: null,
    bankLetter: null,
    serviceAggre: null
  });
  const [allKycData, setAllKycData] = useState([]);
  const [successMessage, setSuccessMessage] = useState('');
  const [kycSuccessMessage, setKycSuccessMessage] = useState('');
  const [showKycModal, setShowKycModal] = useState(false);
  const [pdfRemoveConfirm, setPdfRemoveConfirm] = useState(null);
  const [kycNeedsRefresh, setKycNeedsRefresh] = useState(false);
  const [showInvoiceGeneration, setShowInvoiceGeneration] = useState(false);
  const [invoiceShipment, setInvoiceShipment] = useState(null);
  // An invoice raised against a customer rather than a shipment.
  const [customerInvoiceOpen, setCustomerInvoiceOpen] = useState(false);
  const [customerInvoiceEdit, setCustomerInvoiceEdit] = useState(null);
  const [customerInvoiceListOpen, setCustomerInvoiceListOpen] = useState(false);
  const [invoiceMap, setInvoiceMap] = useState({}); // { awbNumber: [invoiceData, ...] }
  const [allInvoicesList, setAllInvoicesList] = useState([]); // flat list of all invoices
  const [paymentUpdate, setPaymentUpdate] = useState(null); // { shipmentNo, invoice }
  const [paymentFilter, setPaymentFilter] = useState('all'); // all | no_invoice | unpaid | partially_paid | paid
  const [unpaidSince, setUnpaidSince] = useState(null); // Date; only open (unpaid/partial) invoices dated on/after this
  const [showUnpaidSincePicker, setShowUnpaidSincePicker] = useState(false);
  const [overdueDays, setOverdueDays] = useState(''); // invoice still open N+ days after its invoice date
  const [searchAWB, setSearchAWB] = useState('');
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [dateRange, setDateRange] = useState(null);
  const [deleteInvoiceConfirm, setDeleteInvoiceConfirm] = useState(null); // { id, awbNumber }
  const [deletingInvoice, setDeletingInvoice] = useState(false);
  const [kycStatusFilter, setKycStatusFilter] = useState('all');
  
  const openPdfRemoveConfirm = (config) => {
    setPdfRemoveConfirm(config);
  };

  const closePdfRemoveConfirm = () => {
    setPdfRemoveConfirm(null);
  };

  const confirmPdfRemove = async () => {
    if (pdfRemoveConfirm?.onConfirm) {
      const result = await pdfRemoveConfirm.onConfirm();
      if (result === false) return;
    }
    setPdfRemoveConfirm(null);
  };

  // Fetch all KYC data
  const fetchAllKycData = async () => {
    try {
      const response = await apiCall('/awb/get-kyc', {
        method: 'GET'
      });

      const data = await response.json();

      if (response.ok && data.success && data.data) {
        setAllKycData(data.data);
      } else {
        setAllKycData([]);
      }
    } catch (error) {
      console.error('Error fetching KYC data:', error);
      setAllKycData([]);
    }
  };
  
  // Helper functions for data formatting
  const joinArray = (arr) => {
    if (!arr || !Array.isArray(arr)) return '-';
    return arr.filter(item => item !== null && item !== undefined && item !== '').join(' & ') || '-';
  };

  const joinUniqueArray = (arr) => {
    if (!arr || !Array.isArray(arr)) return '-';
    const uniqueValues = [...new Set(arr.filter(item => item !== null && item !== undefined && item !== ''))];
    return uniqueValues.join(' & ') || '-';
  };

  // Helper function to format dates - convert to DD/MM/YYYY
  const formatDates = (arr) => {
    if (!arr || !Array.isArray(arr)) return '-';
    const filtered = arr.filter(item => item !== null && item !== undefined && item !== '');
    
    if (filtered.length === 0) return '-';
    
    // Month mapping
    const monthMap = {
      'JAN': 0, 'FEB': 1, 'MAR': 2, 'APR': 3, 'MAY': 4, 'JUN': 5,
      'JUL': 6, 'AUG': 7, 'SEP': 8, 'OCT': 9, 'NOV': 10, 'DEC': 11
    };
    
    // Convert dates to DD/MM/YYYY format first
    const formattedDates = filtered.map(date => {
      if (!date) return null;
      let dateStr = date;
      // Remove time part if present
      if (typeof date === 'string' && date.includes(' ')) {
        dateStr = date.split(' ')[0];
      }
      
      // Check if already in DD/MM/YYYY format
      const ddmmyyyyPattern = /^(\d{2})\/(\d{2})\/(\d{4})$/;
      const match = dateStr.match(ddmmyyyyPattern);
      if (match) {
        return dateStr; // Already in correct format
      }
      
      // Check for DD-MMM-YYY or DD-MMM-YYYY format (e.g., "15-NOV-025")
      const ddmmmyyyPattern = /^(\d{2})-(\w{3})-(\d{2,4})$/;
      const mmmMatch = dateStr.match(ddmmmyyyPattern);
      if (mmmMatch) {
        const day = parseInt(mmmMatch[1]);
        const monthStr = mmmMatch[2].toUpperCase();
        let year = parseInt(mmmMatch[3]);
        
        // Fix year: 025 -> 2025, 25 -> 2025
        if (year < 100) {
          year += 2000;
        }
        
        const month = monthMap[monthStr];
        if (month !== undefined) {
          return `${String(day).padStart(2, '0')}/${String(month + 1).padStart(2, '0')}/${year}`;
        }
      }
      
      // Parse and format to DD/MM/YYYY
      try {
        const dateObj = new Date(dateStr);
        if (!isNaN(dateObj.getTime())) {
          let day = dateObj.getDate();
          let month = dateObj.getMonth() + 1;
          let year = dateObj.getFullYear();
          
          // Fix for years like 025 (should be 2025)
          if (year < 100) {
            year += 2000;
          }
          
          return `${String(day).padStart(2, '0')}/${String(month).padStart(2, '0')}/${year}`;
        }
      } catch (e) {
        console.error('Date parsing error:', e);
      }
      return null;
    }).filter(d => d !== null);
    
    // Remove duplicate formatted dates only
    const unique = [...new Set(formattedDates)];
    return unique.join(' & ') || '-';
  };

  const parseAndJoinArray = (field) => {
    if (!field) return '-';
    if (Array.isArray(field)) return joinArray(field);
    if (typeof field === 'string') {
      try {
        const parsed = JSON.parse(field);
        return Array.isArray(parsed) ? joinArray(parsed) : (parsed || '-');
      } catch {
        return field;
      }
    }
    return field;
  };

  const parseAndJoinUniqueArray = (field) => {
    if (!field) return '-';
    if (Array.isArray(field)) return joinUniqueArray(field);
    if (typeof field === 'string') {
      try {
        const parsed = JSON.parse(field);
        return Array.isArray(parsed) ? joinUniqueArray(parsed) : (parsed || '-');
      } catch {
        return field;
      }
    }
    return field;
  };
  
  // Fetch customers from API
  useEffect(() => {
    const fetchCustomers = async () => {
      setLoadingCustomers(true);
      
      try {
        const response = await apiCall(API_ENDPOINTS.GET_CUSTOMERS, {
          method: 'GET'
        });

        const data = await response.json();

        if (response.ok && response.status === 200) {
          console.log('Customers API Response:', data);
          console.log('Raw data array:', data.data);
          
          // data is directly an array
          let rawCustomers = data.data || [];
          
          // If rawCustomers is not an array, make it empty
          if (!Array.isArray(rawCustomers)) {
            console.log('rawCustomers is not array:', rawCustomers);
            rawCustomers = [];
          }
          
          const customersList = rawCustomers.map(c => {
            console.log('Mapping customer:', c);
            return {
              id: c.id,
              name: c.our_users || c.custName || c.name || c.username || 'Unknown'
            };
          });
          console.log('Mapped customers list:', customersList);
          setCustomers(customersList);
        } else {
          console.error('Failed to fetch customers:', data);
          // Fallback to mockUsers if API fails
          setCustomers(mockUsers.filter(user => user.role === 'user'));
        }
      } catch (error) {
        console.error('Error fetching customers:', error);
        // Fallback to mockUsers if API fails
        setCustomers(mockUsers.filter(user => user.role === 'user'));
      } finally {
        setLoadingCustomers(false);
      }
    };

    fetchCustomers();
  }, []);

  // awbNumber / blNumber → invoice records, for the table's invoice columns and
  // the payment filters. Asking per customer missed every shipment whose user
  // wasn't in this accountant's customer list — its invoice existed but the row
  // still offered "Generate" — so read the whole list in one call instead.
  const fetchAllInvoices = async () => {
    try {
      const [airData, seaData] = await Promise.all([
        apiCall(API_ENDPOINTS.INVOICE_SUBMIT, { method: 'GET' })
          .then(r => (r.ok ? r.json() : null)).catch(() => null),
        apiCall(API_ENDPOINTS.INVOICE_SUBMIT_SEA, { method: 'GET' })
          .then(r => (r.ok ? r.json() : null)).catch(() => null),
      ]);

      const map = {};
      const flatList = [];
      [airData, seaData].forEach(data => {
        if (!data) return;
        const invoices = Array.isArray(data) ? data : (data.data || data.invoices || []);
        invoices.forEach(inv => {
          flatList.push(inv);
          const shipmentNo = inv.awbNumber || inv.awb_number || inv.blNumber || inv.bl_number;
          if (shipmentNo) {
            if (!map[shipmentNo]) map[shipmentNo] = [];
            map[shipmentNo].push(inv);
          }
        });
      });
      setInvoiceMap(map);
      setAllInvoicesList(flatList);
    } catch (err) {
      console.error('Error fetching invoices for map:', err);
    }
  };

  /* What a settlement changes, the shipment table reads out of these two maps:
     the invoice's payment status and the purchase bill's. Reload both the
     moment the money is put through, so the table is already right when the
     accountant comes back to it. */
  const refreshSettled = () => {
    fetchAllInvoices();
    loadPurMap();
  };

  useEffect(() => {
    fetchAllInvoices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch all shipments  
  useEffect(() => {
    const fetchAllShipments = async () => {
      // Fetch Air Export shipments (always fetch for notifications)
      if (activeTab === 'Air-Export') {
        setShipmentsLoading(true);
      }
        
        try {
          const response = await apiCall(API_ENDPOINTS.GET_ALL_SHIPMENTS, {
            method: 'GET'
          });

          const data = await response.json();

          if (response.ok && response.status === 200) {
            const shipmentsArray = data.data?.shipments || data.shipments || data.data || [];
            
            if (Array.isArray(shipmentsArray)) {
              const formattedShipments = shipmentsArray.map(shipment => ({
                id: shipment.id,
                awbNumber: shipment.awbNumber || shipment.awb || '',
                awbNo: shipment.awbNumber || shipment.awb || '',
                awb_no: shipment.awbNumber || shipment.awb || '',
                username: shipment.username || '',
                customerName: shipment.customerName || '',
                customer: shipment.customerName || shipment.username || '',
                origin: 'DEL',
                destination: shipment.destCode || shipment.destCity || '',
                bkg_date: shipment.bkgDate || shipment.sbDates?.[0] || shipment.created_at || '',
                created_at: shipment.created_at || shipment.bkgDate || shipment.createdAt || '',
                sbNumber: joinArray(shipment.sbNumbers),
                sbDate: formatDates(shipment.sbDates),
                portCode: shipment.portCode || '',
                cityCode: shipment.destCode || '',
                destCode: shipment.destCode || '',
                destCity: shipment.destCity || '',
                destCountry: shipment.destCountry || '',
                flightNo: parseAndJoinUniqueArray(shipment.flightNumbers),
                flightDate: parseAndJoinUniqueArray(shipment.flightDates),
                totalPkt: shipment.numberOfPackages || '-',
                weight: shipment.grossWeight || '-',
                chWeight: shipment.chargeableWeight || '-',
                amount: parseAndJoinArray(shipment.paymentsAmount),
                activity: parseAndJoinArray(shipment.paymentActivity),
                paymentActivity: Array.isArray(shipment.paymentActivity) ? shipment.paymentActivity : (() => { try { return JSON.parse(shipment.paymentActivity || '[]'); } catch { return []; } })(),
                paymentsAmount: Array.isArray(shipment.paymentsAmount) ? shipment.paymentsAmount : (() => { try { return JSON.parse(shipment.paymentsAmount || '[]'); } catch { return []; } })(),
                fob: parseAndJoinArray(shipment.sbFobValues),
                dbk: parseAndJoinArray(shipment.DBK ?? shipment.dbk ?? shipment.dbkClaim) || '-',
                rodtep: parseAndJoinArray(shipment.Rodtep ?? shipment.rodtep ?? shipment.rodtepAmt) || '-',
                rosctl: parseAndJoinArray(shipment.Rosctl ?? shipment.rosctl ?? shipment.rosctlAmt) || '-',
                igst: parseAndJoinArray(shipment.IGST ?? shipment.igst ?? shipment.igstAmt) || '-',
                fcValue: shipment.fcValue || '-',
                fcType: shipment.fcType || '-',
                fcRate: shipment.fcRate || '-',
                buyerName: shipment.buyerName || '-',
                awb: shipment.awbPdf || shipment.awb || '',
                leo: shipment.leoPdf || shipment.leo || '',
                inv: shipment.invPdf || shipment.inv || '',
                invoice: shipment.invoicePdf || shipment.invoice || '',
                pur: shipment.purPdf || shipment.pur || '',
                service: shipment.shipmentType || '',
                status: shipment.leoStatus || shipment.status || 'Pending',
                userId: shipment.userId || shipment.userID || shipment.user_id || shipment.userOd || null,
                gstNo: shipment.gstNo || shipment.gst_no || '',
                invoiceNumber: parseAndJoinArray(shipment.invoiceNumber || shipment.invoice_number) || '',
                invoiceDate: parseAndJoinArray(shipment.invoiceDate || shipment.invoice_date) || '',
                leoDate: parseAndJoinArray(shipment.leoDate || shipment.leo_date) || ''
              }));
              
              // Remove duplicates by AWB number
              const uniqueShipments = formattedShipments.filter((shipment, index, self) =>
                index === self.findIndex((s) => s.awbNumber === shipment.awbNumber)
              );
              
              console.log('Total shipments:', formattedShipments.length, 'Unique:', uniqueShipments.length);
              setShipments(uniqueShipments);
            } else {
              setShipments([]);
            }
          }
        } catch (error) {
          console.error('Error fetching shipments:', error);
          setShipments([]);
        } finally {
          if (activeTab === 'Air-Export') {
            setShipmentsLoading(false);
          }
        }
      
      // Fetch Sea Export shipments (always fetch for notifications)
      if (activeTab === 'Sea-Export') {
        setLoadingSeaExport(true);
      }
        try {
          const response = await apiCall(API_ENDPOINTS.GET_SEA_EXPORT, {
            method: 'GET'
          });
          
          const data = await response.json();
          console.log('Sea Export API Response (Accountant):', data);
          
          if (response.ok && data.success) {
            const formattedShipments = (data.data || []).map(s => ({
              id: s.id,
              portCode: s.port_code || s.portCode || '-',
              sbNo: s.sb_no || s.sbNo || '-',
              sbDate: s.sb_date || s.sbDate || '-',
              pkg: s.pkg || '-',
              gwt: s.gwt || '-',
              portOfLoading: s.port_of_loading || s.portOfLoading || '-',
              stateOfOrigin: s.state_of_origin || s.stateOfOrigin || '-',
              portOfDischargeCode: s.port_of_discharge_code || s.portOfDischargeCode || '-',
              portOfDischarge: s.port_of_discharge || s.portOfDischarge || '-',
              portOfFinalDestinationCode: s.port_of_final_destination_code || s.portOfFinalDestinationCode || '-',
              portOfFinalDestination: s.port_of_final_destination || s.portOfFinalDestination || '-',
              countryOfFinalDestination: s.country_of_final_destination || s.countryOfFinalDestination || '-',
              exporterName: s.exporter_name || s.exporterName || '-',
              exporterAddress: s.exporter_address || s.exporterAddress || '-',
              gstNo: s.gst_no || s.gstNo || '-',
              buyerName: s.buyer_name || s.buyerName || '-',
              consigneeName: s.consignee_name || s.consigneeName || '-',
              mode: s.mode || '-',
              lut: s.lut || '-',
              fobValue: s.fob_value || s.fobValue || '-',
              dbkClaim: s.dbk_claim || s.dbkClaim || '-',
              igstAmt: s.igst_amt || s.igstAmt || '-',
              cessAmt: s.cess_amt || s.cessAmt || '-',
              igstValue: s.igst_value || s.igstValue || '-',
              rodtepAmt: s.rodtep_amt || s.rodtepAmt || '-',
              rosctlAmt: s.rosctl_amt || s.rosctlAmt || '-',
              invNo: s.inv_no || s.invNo || '-',
              invAmt: s.inv_amt || s.invAmt || '-',
              currency: s.currency || '-',
              leoDate: s.leo_date || s.leoDate || '-',
              invoiceNoDt: s.invoice_no_dt || s.invoiceNoDt || '-',
              blNumber: s.bl_number || s.blNumber || '-',
              containerNo: s.container_no || s.containerNo || '-',
              containerQty: s.container_qty || s.containerQty || '-',
              userId: s.user_id || s.userId || s.userID || null,
              leoPdf: s.leo_pdf || s.leoPdf || '',
              blPdf: s.bl_pdf || s.blPdf || '',
              invPlPdf: s.inv_pl_pdf || s.invPlPdf || '',
              invoicePdf: s.invoice_pdf || s.invoicePdf || '',
              purPdf: s.pur_pdf || s.purPdf || '',
              editedBy: s.edited_by || s.editedBy || '-',
              createdAt: s.created_at || s.createdAt || new Date().toISOString(),
              type: 'Sea-Export'
            }));
            
            setSeaExportShipments(formattedShipments);
          }
        } catch (error) {
          console.error('Error fetching sea export shipments:', error);
        } finally {
          if (activeTab === 'Sea-Export') {
            setLoadingSeaExport(false);
          }
        }
    };

    fetchAllShipments();
    
    // Listen for shipment creation events for instant notification
    const handleShipmentCreated = () => {
      console.log('New shipment created - refreshing...');
      fetchAllShipments();
    };
    
    window.addEventListener('shipmentCreated', handleShipmentCreated);
    
    // Listen for localStorage changes (cross-tab communication)
    const handleStorageChange = (e) => {
      if (e.key === 'newShipmentNotification') {
        console.log('New shipment notification received - refreshing...');
        fetchAllShipments();
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    
    return () => {
      window.removeEventListener('shipmentCreated', handleShipmentCreated);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [activeTab]);

  const handleEditShipment = (shipment) => {
    setSelectedShipment(shipment);
    setEditFiles({
      invoice: null,
      pur: null
    });
    setShowEditModal(true);
  };

  const handleFileChange = (field, file) => {
    setEditFiles({
      ...editFiles,
      [field]: file
    });
  };

  const handleSubmitEdit = async (e) => {
    e.preventDefault();
    
    if (!editFiles.invoice && !editFiles.pur) {
      toast.error('Please select at least one PDF to upload');
      return;
    }

    // A PUR belongs to a creditor; saving one without saying which leaves a
    // bill nobody can place later.
    if (editFiles.pur && !editCreditorId) {
      toast.error('Pick the creditor this PUR came from');
      return;
    }

    try {
      // Upload files to S3
      const filesToUpload = {
        invoice: editFiles.invoice || null,
        pur: editFiles.pur || null
      };
      
      const uploadResults = await uploadAccountantShipmentPDFs(filesToUpload, selectedShipment.awbNumber);
      
      if (!uploadResults.success) {
        alert('Failed to upload files to S3: ' + uploadResults.error);
        return;
      }
      
      // Extract S3 keys
      const invoiceS3Key = uploadResults.keys.invoicePdf || null;
      const purS3Key = uploadResults.keys.purPdf || null;
      
      // Get current user info
      const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
      const editedBy = currentUser.username || currentUser.name || 'Unknown';
      
      // Send S3 keys to backend as JSON with new format
      const requestBody = {
        awbNumber: selectedShipment.awbNumber,
        editedBy: editedBy
      };
      if (purS3Key) requestBody.purPdfKey = purS3Key;
      if (invoiceS3Key) requestBody.invoicePdfKey = invoiceS3Key;

      const token = localStorage.getItem('token');
      
      // Determine shipment type (gmr or dcsc)
      const shipmentType = selectedShipment.service || selectedShipment.shipmentType || 'gmr';
      
      const response = await fetch(
        `${API_BASE_URL}/awb/${shipmentType}/accountant`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(requestBody)
        }
      );

      const data = await response.json();

      if (response.ok) {
        setShipments(prevShipments => prevShipments.map(shipment => {
          if (shipment.awbNumber !== selectedShipment.awbNumber) return shipment;
          return {
            ...shipment,
            invoice: invoiceS3Key || shipment.invoice,
            pur: purS3Key || shipment.pur
          };
        }));
        setShowEditModal(false);
        setSelectedShipment(null);
        toast.success('Shipment updated successfully!');
        if (editFiles.pur) readPur(editFiles.pur, editCreditorId, selectedShipment.awbNumber);
        setEditFiles({ invoice: null, pur: null }); setEditCreditorId('');
      } else {
        alert(`Failed to update shipment: ${data.message}`);
      }
    } catch (error) {
      console.error('Error:', error);
      alert('An error occurred while updating the shipment');
    }
  };
  
  // Handle PDF uploads
  const handleUploadPDFs = async (e) => {
    e.preventDefault();
    
    if (!selectedShipment) {
      alert('No shipment selected');
      return;
    }
    
    // Check if at least one file is selected
    if (!editFiles.invoice && !editFiles.pur) {
      toast.error('Please select at least one PDF to upload');
      return;
    }

    // A PUR belongs to a creditor; saving one without saying which leaves a
    // bill nobody can place later.
    if (editFiles.pur && !editCreditorId) {
      toast.error('Pick the creditor this PUR came from');
      return;
    }
    
    try {
      const response = await uploadAccountantShipmentPDFs(editFiles, selectedShipment.awbNumber);
      
      if (response.success) {
        alert('PDFs uploaded successfully!');
        setShowEditModal(false);
        setEditFiles({ invoice: null, pur: null }); setEditCreditorId('');
        setSelectedShipment(null);
        
        // Refresh shipments
        const shipmentsResponse = await apiCall(API_ENDPOINTS.GET_ALL_SHIPMENTS, {
          method: 'GET'
        });
        
        const data = await shipmentsResponse.json();
        
        if (shipmentsResponse.ok && data.success) {
          const shipmentsArray = data.data || [];
          
          if (Array.isArray(shipmentsArray)) {
            const formattedShipments = shipmentsArray.map(shipment => ({
              id: shipment.id || shipment.awbNumber || Math.random().toString(36).substr(2, 9),
              awbNumber: shipment.awbNumber || '-',
              awbNo: shipment.awbNumber || '-',
              username: shipment.username || '',
              customerName: shipment.customerName || '',
              customer: shipment.customerName || shipment.username || '',
              origin: 'DEL',
              destination: shipment.destCode || shipment.destCity || '',
              bkg_date: shipment.bkgDate || shipment.sbDates?.[0] || shipment.created_at || '',
              created_at: shipment.created_at || shipment.bkgDate || '',
              sbNumber: joinArray(shipment.sbNumbers),
              sbDate: formatDates(shipment.sbDates),
              portCode: shipment.portCode || '',
              cityCode: shipment.destCode || '',
              destCode: shipment.destCode || '',
              destCity: shipment.destCity || '',
              destCountry: shipment.destCountry || '',
              flightNo: parseAndJoinUniqueArray(shipment.flightNumbers),
              flightDate: parseAndJoinUniqueArray(shipment.flightDates),
              totalPkt: shipment.numberOfPackages || '-',
              weight: shipment.grossWeight || '-',
              chWeight: shipment.chargeableWeight || '-',
              amount: parseAndJoinArray(shipment.paymentsAmount),
              activity: parseAndJoinArray(shipment.paymentActivity),
              paymentActivity: Array.isArray(shipment.paymentActivity) ? shipment.paymentActivity : (() => { try { return JSON.parse(shipment.paymentActivity || '[]'); } catch { return []; } })(),
              paymentsAmount: Array.isArray(shipment.paymentsAmount) ? shipment.paymentsAmount : (() => { try { return JSON.parse(shipment.paymentsAmount || '[]'); } catch { return []; } })(),
              fob: parseAndJoinArray(shipment.sbFobValues),
              dbk: parseAndJoinArray(shipment.DBK ?? shipment.dbk ?? shipment.dbkClaim) || '-',
              rodtep: parseAndJoinArray(shipment.Rodtep ?? shipment.rodtep ?? shipment.rodtepAmt) || '-',
              rosctl: parseAndJoinArray(shipment.Rosctl ?? shipment.rosctl ?? shipment.rosctlAmt) || '-',
              igst: parseAndJoinArray(shipment.IGST ?? shipment.igst ?? shipment.igstAmt) || '-',
              fcValue: shipment.fcValue || '-',
              fcType: shipment.fcType || '-',
              fcRate: shipment.fcRate || '-',
              buyerName: shipment.buyerName || '-',
              awb: shipment.awbPdf || shipment.awb || '',
              leo: shipment.leoPdf || shipment.leo || '',
              inv: shipment.invPdf || shipment.inv || '',
              invoice: shipment.invoicePdf || shipment.invoice || '',
              pur: shipment.purPdf || shipment.pur || '',
              service: shipment.shipmentType || '',
              status: shipment.leoStatus || shipment.status || 'Pending',
              userId: shipment.userId || shipment.userID || shipment.user_id || shipment.userOd || null,
              gstNo: shipment.gstNo || shipment.gst_no || '',
              invoiceNumber: parseAndJoinArray(shipment.invoiceNumber || shipment.invoice_number) || '',
              invoiceDate: parseAndJoinArray(shipment.invoiceDate || shipment.invoice_date) || '',
              leoDate: parseAndJoinArray(shipment.leoDate || shipment.leo_date) || ''
            }));
            
            // Remove duplicates by AWB number
            const uniqueShipments = formattedShipments.filter((shipment, index, self) =>
              index === self.findIndex((s) => s.awbNumber === shipment.awbNumber)
            );
            
            setShipments(uniqueShipments);
          }
        }
      } else {
        alert('Failed to upload PDFs');
      }
    } catch (error) {
      console.error('Error uploading PDFs:', error);
      alert('Failed to upload PDFs. Please try again.');
    }
  };

  const handleCloseModal = () => {
    setShowEditModal(false);
    setSelectedShipment(null);
    setEditFiles({ invoice: null, pur: null }); setEditCreditorId('');
  };

  const handleSeaExportEdit = (shipment) => {
    setSelectedSeaShipment(shipment);
    setSeaEditFiles({ invoice: null, pur: null }); setSeaEditCreditorId('');
    setShowSeaExportEditModal(true);
  };

  const handleSeaExportUploadPDFs = async (e) => {
    e.preventDefault();
    
    if (!selectedSeaShipment) {
      toast.error('No shipment selected');
      return;
    }
    
    if (!seaEditFiles.invoice && !seaEditFiles.pur) {
      toast.error('Please select at least one PDF to upload');
      return;
    }

    // Same for a sea PUR.
    if (seaEditFiles.pur && !seaEditCreditorId) {
      toast.error('Pick the creditor this PUR came from');
      return;
    }
    
    try {
      // Step 1: Prepare files array for pre-signed URL request
      const files = [];
      if (seaEditFiles.pur) {
        files.push({ type: 'PUR', fileName: seaEditFiles.pur.name });
      }
      if (seaEditFiles.invoice) {
        files.push({ type: 'INVOICE', fileName: seaEditFiles.invoice.name });
      }

      // Step 2: Get pre-signed URLs from backend
      const presignedResponse = await apiCall(API_ENDPOINTS.SEA_EXPORT_ACCOUNTANT_PRESIGNED_URLS, {
        method: 'POST',
        body: JSON.stringify({
          blNumber: selectedSeaShipment.blNumber,
          files: files
        })
      });

      const presignedData = await presignedResponse.json();

      if (!presignedResponse.ok || !presignedData.success) {
        throw new Error(presignedData.message || 'Failed to get upload URLs');
      }

      console.log('Pre-signed URLs received:', presignedData.data);

      // Step 3: Upload files to S3 using pre-signed URLs
      const uploadPromises = [];
      let purPdfKey = null;
      let invoicePdfKey = null;

      if (seaEditFiles.pur && presignedData.data.purPdf) {
        purPdfKey = presignedData.data.purPdf.key;
        uploadPromises.push(
          fetch(presignedData.data.purPdf.uploadUrl, {
            method: 'PUT',
            body: seaEditFiles.pur,
            headers: {
              'Content-Type': seaEditFiles.pur.type
            }
          }).then(res => {
            if (!res.ok) throw new Error('Failed to upload PUR PDF');
            console.log('PUR PDF uploaded successfully');
          })
        );
      }

      if (seaEditFiles.invoice && presignedData.data.invoicePdf) {
        invoicePdfKey = presignedData.data.invoicePdf.key;
        uploadPromises.push(
          fetch(presignedData.data.invoicePdf.uploadUrl, {
            method: 'PUT',
            body: seaEditFiles.invoice,
            headers: {
              'Content-Type': seaEditFiles.invoice.type
            }
          }).then(res => {
            if (!res.ok) throw new Error('Failed to upload Invoice PDF');
            console.log('Invoice PDF uploaded successfully');
          })
        );
      }

      await Promise.all(uploadPromises);

      // Step 4: Update backend with PDF keys
      const updatePayload = {
        blNumber: selectedSeaShipment.blNumber
      };
      if (purPdfKey) updatePayload.purPdfKey = purPdfKey;
      if (invoicePdfKey) updatePayload.invoicePdfKey = invoicePdfKey;

      const updateResponse = await apiCall(API_ENDPOINTS.SEA_EXPORT_ACCOUNTANT_UPDATE, {
        method: 'POST',
        body: JSON.stringify(updatePayload)
      });

      const updateData = await updateResponse.json();

      if (!updateResponse.ok || !updateData.success) {
        throw new Error(updateData.message || 'Failed to update shipment');
      }

      toast.success('PDFs uploaded successfully!');
      if (seaEditFiles.pur) readPur(seaEditFiles.pur, seaEditCreditorId, selectedSeaShipment.blNumber);
      setShowSeaExportEditModal(false);
      setSeaEditFiles({ invoice: null, pur: null }); setSeaEditCreditorId('');
      setSelectedSeaShipment(null);
        
        // Refresh sea export shipments
        const seaResponse = await apiCall(API_ENDPOINTS.GET_SEA_EXPORT, {
          method: 'GET'
        });
        
        const data = await seaResponse.json();
        
        if (seaResponse.ok && data.success) {
          const formattedShipments = (data.data || []).map(s => ({
            id: s.id,
            portCode: s.portCode || '-',
            sbNo: s.sbNo || '-',
            sbDate: s.sbDate || '-',
            pkg: s.pkg || '-',
            gwt: s.gwt || '-',
            portOfLoading: s.portOfLoading || '-',
            stateOfOrigin: s.stateOfOrigin || '-',
            portOfDischargeCode: s.portOfDischargeCode || '-',
            portOfDischarge: s.portOfDischarge || '-',
            portOfFinalDestinationCode: s.portOfFinalDestinationCode || '-',
            portOfFinalDestination: s.port_of_final_destination || s.portOfFinalDestination || '-',
            countryOfFinalDestination: s.country_of_final_destination || s.countryOfFinalDestination || '-',
            exporterName: s.exporter_name || s.exporterName || '-',
            exporterAddress: s.exporter_address || s.exporterAddress || '-',
            gstNo: s.gst_no || s.gstNo || '-',
            buyerName: s.buyer_name || s.buyerName || '-',
            buyerAddress: s.buyer_address || s.buyerAddress || '-',
            consigneeName: s.consignee_name || s.consigneeName || '-',
            mode: s.mode || '-',
            lut: s.lut || '-',
            fobValue: s.fob_value || s.fobValue || '-',
            dbkClaim: s.dbk_claim || s.dbkClaim || '-',
            igstAmt: s.igst_amt || s.igstAmt || '-',
            cessAmt: s.cess_amt || s.cessAmt || '-',
            igstValue: s.igst_value || s.igstValue || '-',
            rodtepAmt: s.rodtep_amt || s.rodtepAmt || '-',
            rosctlAmt: s.rosctl_amt || s.rosctlAmt || '-',
            invNo: s.inv_no || s.invNo || '-',
            invAmt: s.inv_amt || s.invAmt || '-',
            currency: s.currency || '-',
            leoDate: s.leo_date || s.leoDate || '-',
            invoiceNoDt: s.invoice_no_dt || s.invoiceNoDt || '-',
            blNumber: s.bl_number || s.blNumber || '-',
            containerNo: s.container_no || s.containerNo || '-',
            containerQty: s.container_qty || s.containerQty || '-',
            userId: s.user_id || s.userId || s.userID || null,
            leoPdf: s.leo_pdf || s.leoPdf || '',
            blPdf: s.bl_pdf || s.blPdf || '',
            invPlPdf: s.inv_pl_pdf || s.invPlPdf || '',
            invoicePdf: s.invoice_pdf || s.invoicePdf || '',
            purPdf: s.pur_pdf || s.purPdf || '',
            editedBy: s.edited_by || s.editedBy || '-',
            createdAt: s.created_at || s.createdAt || new Date().toISOString(),
            type: 'Sea-Export'
          }));
          
          setSeaExportShipments(formattedShipments);
        }
    } catch (error) {
      console.error('Error uploading sea export PDFs:', error);
      toast.error(`Failed to upload PDFs: ${error.message}`);
    }
  };

  const handleCloseSeaExportModal = () => {
    setShowSeaExportEditModal(false);
    setSelectedSeaShipment(null);
    setSeaEditFiles({ invoice: null, pur: null }); setSeaEditCreditorId('');
  };

  // Fetch all KYC data on mount (when accountant logs in)
  useEffect(() => {
    fetchAllKycData();
  }, []);

  useEffect(() => {
    if (!showKycModal && kycNeedsRefresh) {
      fetchAllKycData();
      setKycNeedsRefresh(false);
    }
  }, [showKycModal, kycNeedsRefresh]);

  // Filter KYC documents when customer is selected
  useEffect(() => {
    if (!selectedCustomer) {
      setExistingKycDocs({
        kyc: null,
        promiseLetter: null,
        bankLetter: null,
        serviceAggre: null
      });
      return;
    }

    const selectedCustomerData = customers.find(c => c.name === selectedCustomer);
    if (!selectedCustomerData) return;

    // Find KYC data for selected customer from already fetched data
    const customerKyc = allKycData.find(kyc => kyc.user_id === selectedCustomerData.id);
    
    if (customerKyc) {
      setExistingKycDocs({
        kyc: customerKyc.kyc_pdf || null,
        promiseLetter: customerKyc.promise_letter_pdf || null,
        bankLetter: customerKyc.bank_letter_pdf || null,
        serviceAggre: customerKyc.service_aggre_pdf || null
      });
    } else {
      setExistingKycDocs({
        kyc: null,
        promiseLetter: null,
        bankLetter: null,
        serviceAggre: null
      });
    }
  }, [selectedCustomer, customers, allKycData]);

  const handleKycFileChange = (field, file) => {
    setKycFiles({
      ...kycFiles,
      [field]: file
    });
  };

  const handleRemoveExistingKyc = async (field) => {
    if (!selectedCustomer) {
      toast.error('Please select a customer first');
      return false;
    }

    const selectedCustomerData = customers.find(c => c.name === selectedCustomer);
    if (!selectedCustomerData) {
      toast.error('Customer not found');
      return false;
    }

    const pdfFieldMap = {
      kyc: 'kyc_letter',
      promiseLetter: 'promise_letter',
      bankLetter: 'bank_letter',
      serviceAggre: 'service_aggrement'
    };

    const pdfField = pdfFieldMap[field];
    if (!pdfField) {
      toast.error('Invalid PDF field');
      return false;
    }

    try {
      const response = await apiCall(`/awb/kyc?userId=${selectedCustomerData.id}`, {
        method: 'DELETE',
        body: JSON.stringify({ pdfField })
      });

      let data = {};
      try {
        data = await response.json();
      } catch {
        data = {};
      }

      if (response.ok) {
        setExistingKycDocs(prev => ({
          ...prev,
          [field]: null
        }));
        toast.success(data.message || 'PDF deleted successfully');
        setKycNeedsRefresh(true);
        return true;
      }

      toast.error(data.message || 'Failed to delete PDF');
      return false;
    } catch (error) {
      console.error('Error deleting KYC PDF:', error);
      toast.error('Failed to delete PDF. Please try again.');
      return false;
    }
  };

  const handleUploadKyc = async () => {
    if (!kycFiles.kyc && !kycFiles.promiseLetter && !kycFiles.bankLetter && !kycFiles.serviceAggre) {
      alert('Please select at least one document to upload');
      return;
    }

    if (!selectedCustomer) {
      alert('Please select a customer first');
      return;
    }

    // Find the selected customer's ID
    const selectedCustomerData = customers.find(c => c.name === selectedCustomer);
    if (!selectedCustomerData) {
      alert('Customer not found');
      return;
    }

    try {
      // Upload KYC files to S3 using dedicated pre-signed URL API
      const uploadResults = await uploadKYCDocuments(
        {
          kyc: kycFiles.kyc || null,
          promiseLetter: kycFiles.promiseLetter || null,
          bankLetter: kycFiles.bankLetter || null,
          serviceAggre: kycFiles.serviceAggre || null
        },
        selectedCustomerData.id
      );
      
      if (!uploadResults.success) {
        alert('Failed to upload KYC files to S3: ' + uploadResults.error);
        return;
      }
      
      // Send S3 keys to backend as JSON
      const requestBody = {
        userId: selectedCustomerData.id
      };
      if (uploadResults.keys.kyc) requestBody.kycPath = uploadResults.keys.kyc;
      if (uploadResults.keys.promiseLetter) requestBody.promiseLetterPath = uploadResults.keys.promiseLetter;
      if (uploadResults.keys.bankLetter) requestBody.bankLetterPath = uploadResults.keys.bankLetter;
      if (uploadResults.keys.serviceAggre) requestBody.serviceAggrementPath = uploadResults.keys.serviceAggre;

      const token = localStorage.getItem('token');
      
      const response = await fetch(
        `${API_BASE_URL}/awb/user/kyc`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(requestBody)
        }
      );

      const data = await response.json();

      if (response.ok) {
        setKycSuccessMessage('KYC documents uploaded successfully!');
        setKycFiles({ kyc: null, promiseLetter: null, bankLetter: null, serviceAggre: null });
        
        // Refresh KYC data after successful upload
        await fetchAllKycData();
        setTimeout(() => {
          setShowKycModal(false);
        }, 2000);
        
        setTimeout(() => setKycSuccessMessage(''), 3000);
      } else {
        alert(data.message || 'Failed to upload KYC documents');
      }
    } catch (error) {
      console.error('Error uploading KYC documents:', error);
      alert('Failed to upload KYC documents. Please try again.');
    }
  };

  const handleSubmitKyc = async (e) => {
    e.preventDefault();
    
    if (!kycFiles.kyc && !kycFiles.promiseLetter && !kycFiles.bankLetter && !kycFiles.serviceAggre) {
      alert('Please select at least one document to upload');
      return;
    }

    if (!selectedCustomer) {
      alert('Please select a customer first');
      return;
    }

    // Find the selected customer's ID
    const selectedCustomerData = customers.find(c => c.name === selectedCustomer);
    if (!selectedCustomerData) {
      alert('Customer not found');
      return;
    }

    try {
      // Upload KYC files to S3 using dedicated pre-signed URL API
      const uploadResults = await uploadKYCDocuments(
        {
          kyc: kycFiles.kyc || null,
          promiseLetter: kycFiles.promiseLetter || null,
          bankLetter: kycFiles.bankLetter || null,
          serviceAggre: kycFiles.serviceAggre || null
        },
        selectedCustomerData.id
      );
      
      if (!uploadResults.success) {
        alert('Failed to upload KYC files to S3: ' + uploadResults.error);
        return;
      }
      
      // Send S3 keys to backend as JSON
      const requestBody = {
        userId: selectedCustomerData.id
      };
      if (uploadResults.keys.kyc) requestBody.kycPath = uploadResults.keys.kyc;
      if (uploadResults.keys.promiseLetter) requestBody.promiseLetterPath = uploadResults.keys.promiseLetter;
      if (uploadResults.keys.bankLetter) requestBody.bankLetterPath = uploadResults.keys.bankLetter;
      if (uploadResults.keys.serviceAggre) requestBody.serviceAggrementPath = uploadResults.keys.serviceAggre;

      const token = localStorage.getItem('token');
      
      const response = await fetch(
        `${API_BASE_URL}/awb/user/kyc`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(requestBody)
        }
      );

      const data = await response.json();

      if (response.ok) {
        setKycSuccessMessage('KYC documents uploaded successfully!');
        setKycFiles({ kyc: null, promiseLetter: null, bankLetter: null, serviceAggre: null });
        
        // Refresh KYC data after successful upload
        await fetchAllKycData();
        
        setTimeout(() => setKycSuccessMessage(''), 3000);
      } else {
        alert(data.message || 'Failed to upload KYC documents');
      }
    } catch (error) {
      console.error('Error uploading KYC documents:', error);
      alert('Failed to upload KYC documents. Please try again.');
    }
  };

  const handleGenerateInvoice = (shipment) => {
    setInvoiceShipment({ ...shipment, existingInvoice: null });
    setShowInvoiceGeneration(true);
  };

  const handleEditInvoice = (shipment, existingInvoice) => {
    setInvoiceShipment({ ...shipment, existingInvoice });
    setShowInvoiceGeneration(true);
  };

  const handleDeleteInvoice = async () => {
    if (!deleteInvoiceConfirm) return;
    const { id, awbNumber } = deleteInvoiceConfirm;
    setDeletingInvoice(true);
    try {
      const response = await apiCall(`${API_ENDPOINTS.INVOICE_DELETE}/${id}`, {
        method: 'DELETE'
      });
      if (response.ok) {
        toast.success('Invoice deleted successfully');
        // Remove from invoiceMap
        setInvoiceMap(prev => {
          const newMap = { ...prev };
          delete newMap[awbNumber];
          return newMap;
        });
        // Clear invoice field from shipment in local state
        setShipments(prev => prev.map(s =>
          s.awbNumber === awbNumber ? { ...s, invoice: null } : s
        ));
        if (selectedShipment?.awbNumber === awbNumber) {
          setSelectedShipment(prev => ({ ...prev, invoice: null }));
        }
        setDeleteInvoiceConfirm(null);
      } else {
        const data = await response.json().catch(() => ({}));
        toast.error(data.message || 'Failed to delete invoice');
      }
    } catch (error) {
      console.error('Error deleting invoice:', error);
      toast.error('Failed to delete invoice');
    } finally {
      setDeletingInvoice(false);
    }
  };
  
  const normalizeValue = (value) => String(value || '').trim().toLowerCase();

  const getAllTimestamps = (value) => {
    if (!value) return [];

    const normalizeValues = (val) => {
      if (!val) return [];
      if (Array.isArray(val)) return val;
      if (typeof val === 'string') {
        const trimmed = val.trim();
        try {
          const parsed = JSON.parse(trimmed);
          if (Array.isArray(parsed)) return parsed;
        } catch {
          // ignore
        }
        if (trimmed.includes(' & ')) return trimmed.split(' & ');
        if (trimmed.includes(',')) return trimmed.split(',');
        return [trimmed];
      }
      return [val];
    };

    const toTimestamp = (val) => {
      if (!val) return 0;
      const str = String(val).trim();

      const ddmmyyyy = str.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
      if (ddmmyyyy) {
        const [, dd, mm, yyyy] = ddmmyyyy;
        return new Date(`${yyyy}-${mm}-${dd}`).getTime() || 0;
      }

      const ddmmyyDash = str.match(/^(\d{2})-(\d{2})-(\d{2,4})$/);
      if (ddmmyyDash) {
        let [, dd, mm, yy] = ddmmyyDash;
        let yyyy = parseInt(yy, 10);
        if (yyyy < 100) yyyy += 2000;
        return new Date(`${yyyy}-${mm}-${dd}`).getTime() || 0;
      }

      const ddmmmyy = str.match(/^(\d{2})-(\w{3})-(\d{2,4})$/);
      if (ddmmmyy) {
        const monthMap = {
          JAN: '01', FEB: '02', MAR: '03', APR: '04', MAY: '05', JUN: '06',
          JUL: '07', AUG: '08', SEP: '09', OCT: '10', NOV: '11', DEC: '12'
        };
        const dd = ddmmmyy[1];
        const mmm = ddmmmyy[2].toUpperCase();
        let yyyy = parseInt(ddmmmyy[3], 10);
        if (yyyy < 100) yyyy += 2000;
        const mm = monthMap[mmm];
        if (mm) return new Date(`${yyyy}-${mm}-${dd}`).getTime() || 0;
      }

      const parsed = new Date(str).getTime();
      return Number.isNaN(parsed) ? 0 : parsed;
    };

    return normalizeValues(value).map(toTimestamp).filter((ts) => ts > 0);
  };

  const inSelectedDateRange = (value) => {
    if (!dateRange || !dateRange[0]?.startDate || !dateRange[0]?.endDate) return true;
    const startDate = new Date(dateRange[0].startDate);
    const endDate = new Date(dateRange[0].endDate);
    startDate.setHours(0, 0, 0, 0);
    endDate.setHours(23, 59, 59, 999);
    const startTs = startDate.getTime();
    const endTs = endDate.getTime();
    const timestamps = getAllTimestamps(value);
    if (!timestamps.length) return false;
    return timestamps.some((ts) => ts >= startTs && ts <= endTs);
  };

  const formatDateRange = () => {
    if (!dateRange || !dateRange[0]) return 'Select date range';
    const start = dateRange[0].startDate;
    const end = dateRange[0].endDate;
    return `${start.toLocaleDateString()} - ${end.toLocaleDateString()}`;
  };

  const customerMatches = (values = []) => {
    if (!selectedCustomer) return true;
    const selected = normalizeValue(selectedCustomer);
    return values.some((value) => normalizeValue(value) === selected);
  };

  const matchesPaymentFilter = makePaymentMatcher({ invoiceMap, paymentFilter, unpaidSince, overdueDays });

  // Air Export filters (customer + invoice missing + payment)
  const filteredAirShipments = shipments
    .filter((s) => customerMatches([s.username, s.customerName, s.customer]))
    .filter((s) => matchesShipmentSearch(s, searchAWB))
    .filter((s) => inSelectedDateRange(s.sbDate || s.createdAt || s.updatedAt))
    .filter((s) => matchesPaymentFilter(s.awbNumber, !!s.invoice));

  // Sea Export filters (customer)
  const filteredSeaExportShipments = seaExportShipments.filter((s) =>
    customerMatches([s.exporterName, s.customerName, s.username])
  )
    .filter((s) => matchesShipmentSearch(s, searchAWB))
    .filter((s) => inSelectedDateRange(s.sbDate || s.createdAt || s.leoDate))
    .filter((s) => matchesPaymentFilter(s.blNumber || s.awbNumber, !!s.invoice));

  const shipmentScopeLabel = selectedCustomer || 'All Users';
  const shipmentHeading = selectedCustomer ? selectedCustomer : 'All Users Shipments';
  const totalVisibleShipments = activeTab === 'Air-Export'
    ? filteredAirShipments.length
    : filteredSeaExportShipments.length;

  if (customerInvoiceOpen) {
    return (
      <InvoiceGenerationPage
        customerInvoice
        customerUsers={customers}
        existingInvoice={customerInvoiceEdit}
        allInvoices={allInvoicesList}
        onClose={() => {
          setCustomerInvoiceOpen(false);
          setCustomerInvoiceEdit(null);
          fetchAllInvoices();
        }}
      />
    );
  }

  // If invoice generation is active, show only that page
  if (showInvoiceGeneration && invoiceShipment) {
    return (
      <InvoiceGenerationPage
        shipment={invoiceShipment}
        existingInvoice={invoiceShipment?.existingInvoice || null}
        allInvoices={allInvoicesList}
        onClose={() => {
          setShowInvoiceGeneration(false);
          setInvoiceShipment(null);
          // Re-fetch invoices so Edit Invoice button appears immediately
          fetchAllInvoices();
        }}
      />
    );
  }


  /* Purchase and banking are a different kind of job from raising an invoice,
     so the two buttons that open them read as their own pair: one solid, one
     quiet, both in violet, with the icon on a tile of its own. */
  /* Reconciliation is a different job from raising an invoice, so this one
     carries its own colour rather than joining the blue and green row. */
  const bankBtn =
    'inline-flex items-center gap-2.5 h-10 pl-2 pr-4 rounded-xl text-sm font-bold text-white ' +
    'bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-500 hover:to-indigo-500 ' +
    'shadow-lg shadow-violet-500/25 hover:shadow-xl hover:shadow-violet-500/30 ' +
    'motion-safe:transition motion-safe:duration-200 motion-safe:hover:-translate-y-px ' +
    'focus:outline-none focus-visible:ring-4 focus-visible:ring-violet-500/30';

  /**
   * Send the PUR to be read, once it is safely filed.
   *
   * The PDF is already uploaded by the time this runs, so a read that fails
   * must not read as a failed save - it is reported on its own terms and the
   * shipment keeps its document either way.
   */
  const readPur = async (file, creditorId, awbNumber) => {
    const creditor = creditorList.find((c) => String(c.id) === String(creditorId));
    if (!file || !creditor) return;
    const t = toast.loading(`Reading the bill from ${creditor.name}…`);
    try {
      const r = await extractPur({ creditorId, creditorName: creditor.name, awbNumber, file });
      if (r.duplicate) {
        toast(r.message, { id: t, icon: 'ℹ️' });
      } else if (!r.ok) {
        toast.error(`PDF saved, but it could not be read — ${r.message}`, { id: t, duration: 6000 });
      } else if (r.extractionStatus === 'needs_review') {
        toast(`Read ${r.lines} line${r.lines === 1 ? '' : 's'} from ${r.invoiceNumber || 'the bill'} — needs review`,
          { id: t, icon: '⚠️', duration: 6000 });
      } else {
        toast.success(`Read ${r.lines} line${r.lines === 1 ? '' : 's'} from ${r.invoiceNumber || 'the bill'}`, { id: t });
        loadPurMap();
      }
    } catch (err) {
      toast.error(`PDF saved, but it could not be read — ${err.message}`, { id: t, duration: 6000 });
    }
  };

  const navSections = [
    { title: 'Export', items: [
      { id: 'Air-Export', label: 'Air Export', icon: Plane },
      { id: 'Sea-Export', label: 'Sea Export', icon: Ship },
    ] },
    { title: 'Import', items: [
      { id: 'Air-Import', label: 'Air Import', icon: Plane },
      { id: 'Sea-Import', label: 'Sea Import', icon: Ship },
    ] },
    { title: 'Finance', items: [
      { id: 'Clients', label: 'Client Master', icon: Users },
      { id: 'HSN',     label: 'HSC Codes',    icon: Hash },
    ] },
    { title: 'Compliance', items: [
      { id: 'KYC', label: 'KYC Documents', icon: FileText },
    ] },
  ];

  // The topbar box searches whatever tab is open, writing to the same state
  // that tab's own box uses; a tab with nothing to search gets no box.
  const tabSearch = {
    'Air-Export': { value: searchAWB,    set: setSearchAWB,    hint: 'Search AWB, S/B, user, consignee…' },
    'Sea-Export': { value: searchAWB,    set: setSearchAWB,    hint: 'Search B/L, S/B, exporter, port…' },
    Clients:      { value: clientSearch, set: setClientSearch, hint: 'Search client, GSTIN or address…' },
    HSN:          { value: hsnSearch,    set: setHsnSearch,    hint: 'Search HSC code or service…' },
    KYC:          { value: kycSearch,    set: setKycSearch,    hint: 'Search a user by name…' },
    Bank:         { value: bankSearch,     set: setBankSearch,     hint: 'Search a name or a remark…' },
  }[activeTab] || null;

  return (
    <AdminShell
      sections={navSections}
      activeId={activeTab}
      onSelect={setActiveTab}
      role="Accountant"
      search={tabSearch?.value ?? ''}
      onSearch={tabSearch?.set}
      searchPlaceholder={tabSearch?.hint}
    >
      <div className="max-w-[1600px] mx-auto">
        {/* KYC Tab */}
        {activeTab === 'KYC' && (() => {
          // Build KYC rows: all customers joined with their KYC data
          const kycRows = customers.map((customer, idx) => {
            const kyc = allKycData.find(k => k.user_id === customer.id) || {};
            const docs = {
              kyc: kyc.kyc_pdf || null,
              promiseLetter: kyc.promise_letter_pdf || null,
              bankLetter: kyc.bank_letter_pdf || null,
              serviceAggre: kyc.service_aggre_pdf || null
            };
            const uploadedCount = Object.values(docs).filter(Boolean).length;
            const status = uploadedCount === 4 ? 'complete' : uploadedCount > 0 ? 'in-progress' : 'pending';
            return { ...customer, docs, status, idx: idx + 1 };
          });

          const kycTerm = kycSearch.trim().toLowerCase();
          const filteredKycRows = kycRows
            .filter(r => kycStatusFilter === 'all' || r.status === kycStatusFilter)
            .filter(r => !kycTerm || String(r.name || '').toLowerCase().includes(kycTerm));

          const statusCounts = {
            all: kycRows.length,
            complete: kycRows.filter(r => r.status === 'complete').length,
            'in-progress': kycRows.filter(r => r.status === 'in-progress').length,
            pending: kycRows.filter(r => r.status === 'pending').length
          };

          return (
            <div>
              <PageHead
                eyebrow="Accountant · Compliance"
                title="KYC Documents"
                badge={<Pill tone="blue">{kycRows.length} users</Pill>}
                subtitle="The four documents each user has to file, and what is still missing."
                actions={(
                  <button type="button" className={btn.primary} onClick={() => setShowKycModal(true)}>
                    <Upload size={16} /> Upload KYC
                  </button>
                )}
              />

              <Panel className="overflow-hidden">
                <div className="flex flex-wrap items-center gap-2 px-4 sm:px-5 py-4 border-b border-slate-100 dark:border-slate-800">
                  {[
                    { key: 'all',         label: 'All',         tone: 'blue' },
                    { key: 'complete',    label: 'Complete',    tone: 'emerald' },
                    { key: 'in-progress', label: 'In progress', tone: 'amber' },
                    { key: 'pending',     label: 'Pending',     tone: 'rose' },
                  ].map((f) => {
                    const on = kycStatusFilter === f.key;
                    const ring = {
                      blue: 'border-blue-400 bg-blue-50 text-blue-700 dark:border-blue-500/60 dark:bg-blue-500/15 dark:text-blue-300',
                      emerald: 'border-emerald-400 bg-emerald-50 text-emerald-700 dark:border-emerald-500/60 dark:bg-emerald-500/15 dark:text-emerald-300',
                      amber: 'border-amber-400 bg-amber-50 text-amber-700 dark:border-amber-500/60 dark:bg-amber-500/15 dark:text-amber-300',
                      rose: 'border-rose-400 bg-rose-50 text-rose-700 dark:border-rose-500/60 dark:bg-rose-500/15 dark:text-rose-300',
                    }[f.tone];
                    return (
                      <button key={f.key} type="button" onClick={() => setKycStatusFilter(f.key)}
                        className={`inline-flex items-center gap-2 h-10 px-3.5 rounded-xl border text-[13px] font-bold transition ${
                          on ? ring
                             : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800/60 dark:text-slate-300 dark:hover:bg-slate-700/60'}`}>
                        {f.label}
                        <span className={`px-1.5 rounded-md text-[11px] ${on ? 'bg-white/60 dark:bg-white/10' : 'bg-slate-100 dark:bg-slate-700'}`}>
                          {statusCounts[f.key]}
                        </span>
                      </button>
                    );
                  })}
                </div>

                <Table
                  head={[
                    { label: 'S.No' }, { label: 'User name' }, { label: 'KYC document' },
                    { label: 'Premise letter' }, { label: 'Bank letter' }, { label: 'Service agreement' },
                    { label: 'Status' }, { label: 'Action' },
                  ]}
                  empty="No user matches this filter"
                >
                  {filteredKycRows.map((row, i) => {
                    const tone = { complete: 'emerald', 'in-progress': 'amber', pending: 'rose' }[row.status];
                    const label = { complete: 'Complete', 'in-progress': 'In progress', pending: 'Pending' }[row.status];

                    // Filed documents open; missing ones just read as missing.
                    const DocIcon = ({ url }) => url ? (
                      <button
                        type="button"
                        onClick={() => window.open(getCloudFrontUrl(url), '_blank')}
                        title="View document"
                        className="inline-flex items-center justify-center w-8 h-8 rounded-lg transition
                                   bg-emerald-50 text-emerald-600 hover:bg-emerald-100
                                   dark:bg-emerald-500/15 dark:text-emerald-300 dark:hover:bg-emerald-500/25"
                      >
                        <FileText size={15} />
                      </button>
                    ) : (
                      <span title="Not filed yet"
                        className="inline-flex items-center justify-center w-8 h-8 rounded-lg
                                   bg-slate-100 text-slate-400 dark:bg-slate-800 dark:text-slate-600">
                        <X size={15} />
                      </span>
                    );

                    return (
                      <Row key={row.id}>
                        <Cell className="font-mono text-[12px] text-slate-500 dark:text-slate-400">{i + 1}</Cell>
                        <Cell className="whitespace-nowrap">
                          <div className="flex items-center gap-2.5 min-w-0">
                            <Avatar name={row.name} tone={tone} />
                            <span className="font-bold text-slate-900 dark:text-white truncate">{row.name}</span>
                          </div>
                        </Cell>
                        <Cell><DocIcon url={row.docs.kyc} /></Cell>
                        <Cell><DocIcon url={row.docs.promiseLetter} /></Cell>
                        <Cell><DocIcon url={row.docs.bankLetter} /></Cell>
                        <Cell><DocIcon url={row.docs.serviceAggre} /></Cell>
                        <Cell><Pill tone={tone} dot>{label}</Pill></Cell>
                        <Cell>
                          <button
                            type="button"
                            onClick={() => { setSelectedCustomer(row.name); setShowKycModal(true); }}
                            className={`${btn.quiet} h-9 px-3 text-[12.5px]`}
                          >
                            <Upload size={14} /> Upload
                          </button>
                        </Cell>
                      </Row>
                    );
                  })}
                </Table>
              </Panel>
            </div>
          );
        })()}

        {activeTab === 'HSN' && <HsnCodeManager search={hsnSearch} onSearch={setHsnSearch} />}

        {activeTab === 'Bank' && <BankStatement search={bankSearch} onBack={() => setActiveTab('Air-Export')} onSettled={refreshSettled} />}

        {activeTab === 'Clients' && (
          <ClientMaster
            role="accountant"
            search={clientSearch}
            onSearch={setClientSearch}
            customers={customers}
            onEditInvoice={(invoice) => { setCustomerInvoiceEdit(invoice); setCustomerInvoiceOpen(true); }}
          />
        )}

        {/* Coming Soon for other tabs */}
        {!['KYC', 'HSN', 'Clients', 'Air-Export', 'Sea-Export', 'Bank'].includes(activeTab) ? (
          <div className="flex items-center justify-center min-h-[60vh]">
            <div className="text-center">
              <div className="mb-6">
                {activeTab === 'Air-Import' && <Plane size={80} className="mx-auto text-blue-500 dark:text-blue-400" />}
                {activeTab === 'Sea-Import' && <Ship size={80} className="mx-auto text-teal-500 dark:text-teal-400" />}
              </div>
              <h2 className="text-3xl font-bold text-slate-800 dark:text-slate-200 mb-4">
                Coming Soon
              </h2>
              <p className="text-slate-600 dark:text-slate-400 text-lg">
                {activeTab} feature is under development
              </p>
            </div>
          </div>
        ) : activeTab === 'Air-Export' || activeTab === 'Sea-Export' ? (
          <>
        {(() => {
          const isAir = activeTab === 'Air-Export';
          const rows = isAir ? filteredAirShipments : filteredSeaExportShipments;
          const book = isAir ? shipments : seaExportShipments;

          const billed = (x) => (invoiceMap[x.awbNumber] || invoiceMap[x.blNumber] || []).length > 0;
          const invoiced = book.filter(billed).length;
          const pct = (n) => (book.length ? `${Math.round((n / book.length) * 100)}%` : '—');

          const fobOf = (x) => String((isAir ? x.fob : x.fobValue) ?? '')
            .split('&')
            .reduce((sum, part) => sum + (parseFloat(String(part).replace(/[^\d.-]/g, '')) || 0), 0);
          const fob = book.reduce((sum, x) => sum + fobOf(x), 0);
          const shortInr = (n) => (n >= 1e7 ? `₹${(n / 1e7).toFixed(2)} Cr`
            : n >= 1e5 ? `₹${(n / 1e5).toFixed(2)} L`
            : `₹${Math.round(n).toLocaleString('en-IN')}`);

          const facets = [
            ...(selectedCustomer ? [{ key: 'u', label: `User: ${selectedCustomer}`, clear: () => setSelectedCustomer('') }] : []),
            ...(searchAWB.trim() ? [{ key: 'q', label: `Search: ${searchAWB.trim()}`, clear: () => setSearchAWB('') }] : []),
            ...(dateRange ? [{ key: 'd', label: `Dates: ${formatDateRange()}`, clear: () => setDateRange(null) }] : []),
            ...(paymentFilter !== 'all' ? [{ key: 'p', label: `Billing: ${paymentFilter.replace(/_/g, ' ')}`, clear: () => setPaymentFilter('all') }] : []),
            ...(unpaidSince ? [{ key: 's', label: `Unpaid since ${unpaidSince.toLocaleDateString('en-GB')}`, clear: () => setUnpaidSince(null) }] : []),
            ...(overdueDays !== '' ? [{ key: 'o', label: `Overdue by ${overdueDays}d`, clear: () => setOverdueDays('') }] : []),
          ];
          const resetAll = () => {
            setSelectedCustomer(''); setSearchAWB(''); setDateRange(null);
            setPaymentFilter('all'); setUnpaidSince(null); setOverdueDays('');
          };

          const miniStats = [
            { id: 'total', label: isAir ? 'Air consignments' : 'Sea consignments', value: book.length,
              note: `${rows.length} match the filters`,
              bar: isAir ? 'bg-cyan-500' : 'bg-emerald-500',
              tone: isAir ? 'text-cyan-600 dark:text-cyan-300' : 'text-emerald-600 dark:text-emerald-300' },
            { id: 'billed', label: 'Invoiced', value: invoiced, note: pct(invoiced),
              bar: 'bg-blue-500', tone: 'text-blue-600 dark:text-blue-300' },
            { id: 'unbilled', label: 'Awaiting invoice', value: book.length - invoiced, note: pct(book.length - invoiced),
              bar: 'bg-amber-500', tone: 'text-amber-600 dark:text-amber-300',
              on: paymentFilter === 'no_invoice',
              apply: () => setPaymentFilter(paymentFilter === 'no_invoice' ? 'all' : 'no_invoice') },
            { id: 'fob', label: 'FOB value', value: shortInr(fob),
              note: `across ${book.length} ${isAir ? 'AWBs' : 'B/Ls'}`,
              bar: 'bg-violet-500', tone: 'text-violet-600 dark:text-violet-300' },
          ];

          return (
            <>
              <PageHead
                eyebrow={`Accountant · ${isAir ? 'Air export' : 'Sea export'}`}
                title={isAir ? 'Air Export' : 'Sea Export'}
                badge={<Pill tone="emerald" dot>{book.length} consignments</Pill>}
                subtitle={selectedCustomer
                  ? `Consignments raised by ${selectedCustomer}.`
                  : 'Every consignment waiting to be billed, and the ones already invoiced.'}
                actions={(
                  <>
                    {isAir && (
                      <button type="button" className={bankBtn}
                        title="Read a bank statement and settle what it clears"
                        onClick={() => setActiveTab('Bank')}>
                        <span className="grid place-items-center w-7 h-7 rounded-lg bg-white/20 flex-shrink-0">
                          <FileSpreadsheet size={15} />
                        </span>
                        Bank Statement
                      </button>
                    )}
                    <button type="button" className={btn.primary}
                      title="Raise an invoice against a customer, without a shipment"
                      onClick={() => setCustomerInvoiceOpen(true)}>
                      <FileText size={16} /> Generate invoice
                    </button>
                    <button type="button" className={btn.quiet}
                      title="The invoices raised against a customer, with no shipment"
                      onClick={() => setCustomerInvoiceListOpen(true)}>
                      <Receipt size={16} /> Customer invoices
                    </button>
                    {isAir && (
                      <button type="button" className={btn.green}
                        onClick={() => exportShipmentsExcel(filteredAirShipments, invoiceMap)}>
                        <Download size={16} /> Export Excel
                      </button>
                    )}
                  </>
                )}
              />

              <Panel className="px-4 sm:px-5 py-4 mb-5">
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                  {miniStats.map((m) => (
                    <button key={m.id} type="button" onClick={m.apply || undefined} disabled={!m.apply}
                      title={m.apply ? `Show ${m.label.toLowerCase()}` : undefined}
                      className={`relative overflow-hidden rounded-xl border px-3.5 pt-2.5 pb-3 text-left transition
                                  ${!m.apply ? 'cursor-default ' : ''}${m.on
                                    ? 'border-blue-400 bg-blue-50 dark:border-blue-500/60 dark:bg-blue-500/10 ring-2 ring-blue-500/20'
                                    : 'border-slate-200 bg-slate-50/70 hover:bg-slate-100 dark:border-slate-800 dark:bg-slate-900/40 dark:hover:bg-slate-800/60'}`}>
                      <p className="text-[10px] font-black uppercase tracking-[0.12em] text-slate-400 dark:text-slate-500">{m.label}</p>
                      <div className={`mt-1 ${m.apply ? 'flex items-baseline gap-2' : ''}`}>
                        <span className={`leading-none font-black text-slate-900 dark:text-white
                                          ${m.apply ? 'text-[22px]' : 'text-[19px]'}`}>{m.value}</span>
                        <span className={`block text-[11px] font-bold ${m.tone} ${m.apply ? '' : 'mt-1'}`}>{m.note}</span>
                      </div>
                      <span className={`absolute left-0 right-0 bottom-0 h-[3px] ${m.bar}`} />
                    </button>
                  ))}
                </div>
              </Panel>

              <ShipmentFilters
                user={{
                  value: selectedCustomer,
                  onChange: setSelectedCustomer,
                  options: customers.map((c) => ({ value: c.name, label: c.name })),
                }}
                dateLabel={dateRange ? formatDateRange() : 'All dates'}
                onOpenDates={() => setShowDatePicker(true)}
                paymentFilter={paymentFilter}
                onPaymentFilter={setPaymentFilter}
                unpaidSince={unpaidSince}
                onUnpaidSince={setUnpaidSince}
                unpaidOpen={showUnpaidSincePicker}
                onUnpaidOpen={setShowUnpaidSincePicker}
                overdueDays={overdueDays}
                onOverdueDays={setOverdueDays}
                facets={facets}
                onReset={resetAll}
              />
            </>
          );
        })()}

        {shipmentsLoading ? (
          <div className="bg-white dark:bg-slate-800 rounded-lg shadow p-12 flex items-center justify-center">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-slate-600 dark:text-slate-400">Loading shipments...</p>
            </div>
          </div>
        ) : activeTab === 'Sea-Export' ? (
          <SeaExportTableReadOnly
            shipments={filteredSeaExportShipments}
            loading={loadingSeaExport}
            onEdit={handleSeaExportEdit}
            onGenerateInvoice={handleGenerateInvoice}
            onEditInvoice={handleEditInvoice}
            invoiceMap={invoiceMap}
            userRole="accountant"
            highlightedBl={highlightedShipment?.type === 'Sea-Export' ? highlightedShipment.id : null}
          />
        ) : (
          <ShipmentsTable 
            shipments={filteredAirShipments} 
            userRole="accountant" 
            onEdit={handleEditShipment}
            onGenerateInvoice={handleGenerateInvoice}
            onEditInvoice={handleEditInvoice}
            onUpdatePayment={(shipment, invoice) => setPaymentUpdate({ shipmentNo: shipment.awbNumber, invoice })}
            showTotals
            invoiceMap={invoiceMap}
            purMap={purMap}
            onViewPur={(pur) => setPurViewing(pur)}
            highlightedAwb={highlightedShipment?.type === 'Air-Export' ? highlightedShipment.id : null}
          />
        )}
          </>
        ) : null}
      </div>

      {/* One purchase bill, opened from the shipment it was filed against. */}
      <PurViewerModal
        open={Boolean(purViewing)}
        onClose={() => setPurViewing(null)}
        documentId={purViewing?.id}
      />

      {/* Reachable from any shipment screen; the KYC page has its own. */}
      {activeTab !== 'KYC' && (
        <button
          onClick={() => setShowKycModal(true)}
          className="fixed bottom-7 right-7 z-40 inline-flex items-center gap-2.5 px-5 h-12 rounded-2xl
                     text-[13.5px] font-bold text-white bg-blue-600 hover:bg-blue-700
                     shadow-lg shadow-blue-600/25 transition hover:-translate-y-0.5"
          title="Upload KYC documents"
        >
          <Upload size={18} />
          Upload KYC
        </button>
      )}

      {/* Edit Modal for PDF Upload */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-slate-800 rounded-lg max-w-xl w-full overflow-hidden">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-blue-600 via-blue-700 to-indigo-700 px-6 py-5">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 bg-white/15 backdrop-blur-sm rounded-xl flex items-center justify-center">
                    <Plane size={22} className="text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-white tracking-wide">Air Export Documents</h3>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="px-2 py-0.5 bg-white/20 text-white text-xs font-semibold rounded-full">
                        AWB {selectedShipment?.awbNumber}
                      </span>
                      <span className="px-2 py-0.5 bg-sky-400/30 text-sky-100 text-xs font-medium rounded-full">
                        {selectedShipment?.service || 'Air Export'}
                      </span>
                    </div>
                  </div>
                </div>
                <button
                  onClick={handleCloseModal}
                  className="p-1.5 bg-white/10 hover:bg-white/25 rounded-lg text-white transition"
                >
                  <X size={18} />
                </button>
              </div>
            </div>

            
            {/* Modal Body */}
            <form onSubmit={handleSubmitEdit} className="p-6">
              <div className="space-y-6">
                <h4 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">Document Uploads</h4>
                
                {/* Which creditor's bill this PUR is. A PUR always comes from one,
                    and the reader needs to know whose layout to expect. */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                    Creditor {editFiles.pur && <span className="text-rose-500">*</span>}
                  </label>
                  <select
                    value={editCreditorId}
                    onChange={(e) => setEditCreditorId(e.target.value)}
                    className="ql-select w-full px-3 py-2.5 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition"
                  >
                    <option value="">Select the creditor who sent this bill…</option>
                    {creditorList.map((c) => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                  {editFiles.pur && !editCreditorId && (
                    <p className="mt-1.5 text-[11.5px] font-semibold text-amber-600 dark:text-amber-400">
                      Pick the creditor this PUR came from.
                    </p>
                  )}
                </div>

                {/* PUR PDF */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                    PUR
                  </label>
                  
                  {/* Show existing PDF */}
                  {(selectedShipment?.pur && !editFiles.pur) ? (
                    <div className="p-3 bg-blue-50 dark:bg-blue-900/25 border-2 border-blue-200 dark:border-blue-700/50 rounded-lg">
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => {
                            // The PDF sits on the CDN, not on the API host. A re-upload
                            // keeps the same key, so the version defeats the cache - the
                            // same way the invoice beside this one is opened.
                            const fullUrl = getCloudFrontUrl(selectedShipment.pur);
                            const version = selectedShipment.updatedAt ? new Date(selectedShipment.updatedAt).getTime() : Date.now();
                            window.open(`${fullUrl}${fullUrl.includes('?') ? '&' : '?'}v=${version}`, '_blank');
                          }}
                          className="flex-1 px-3 py-2 text-sm font-medium text-blue-700 dark:text-blue-300 bg-blue-100 dark:bg-blue-800 hover:bg-blue-200 dark:hover:bg-blue-700 rounded-md transition flex items-center justify-center gap-2"
                        >
                          <FileText size={18} className="flex-shrink-0" />
                          View
                        </button>
                        <button
                          type="button"
                          onClick={() => openPdfRemoveConfirm({
                            title: 'Remove PDF?',
                            message: 'Are you sure you want to remove this PUR PDF?',
                            onConfirm: () => setSelectedShipment(prev => ({ ...prev, pur: null }))
                          })}
                          className="px-3 py-2 text-sm font-medium text-red-700 dark:text-red-300 bg-red-100 dark:bg-red-900/40 hover:bg-red-200 dark:hover:bg-red-800/60 rounded-md transition flex items-center gap-1"
                          title="Remove"
                        >
                          <X size={18} className="flex-shrink-0" />
                          Clear
                        </button>
                      </div>
                    </div>
                  ) : editFiles.pur ? (
                    <div className="p-3 bg-green-50 dark:bg-green-900/25 border-2 border-green-200 dark:border-green-700/50 rounded-lg">
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => {
                            const fileURL = URL.createObjectURL(editFiles.pur);
                            window.open(fileURL, '_blank');
                          }}
                          className="flex-1 px-3 py-2 text-sm font-medium text-green-700 dark:text-green-300 bg-green-100 dark:bg-green-800 hover:bg-green-200 dark:hover:bg-green-700 rounded-md transition flex items-center justify-center gap-2"
                        >
                          <FileText size={18} className="flex-shrink-0" />
                          View
                        </button>
                        <button
                          type="button"
                          onClick={() => openPdfRemoveConfirm({
                            title: 'Remove PDF?',
                            message: 'Are you sure you want to remove this PUR PDF?',
                            onConfirm: () => setEditFiles(prev => ({ ...prev, pur: null }))
                          })}
                          className="px-3 py-2 text-sm font-medium text-red-700 dark:text-red-300 bg-red-100 dark:bg-red-900/40 hover:bg-red-200 dark:hover:bg-red-800/60 rounded-md transition flex items-center gap-1"
                          title="Remove"
                        >
                          <X size={18} className="flex-shrink-0" />
                          Clear
                        </button>
                      </div>
                    </div>
                  ) : (
                    <label className="flex items-center justify-center gap-2 p-3 border-2 border-dashed border-slate-300 dark:border-slate-600 rounded-lg cursor-pointer hover:border-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition">
                      <Upload size={18} className="text-slate-400" />
                      <span className="text-sm text-slate-600 dark:text-slate-400">Upload PDF</span>
                      <input
                        type="file"
                        accept=".pdf"
                        onChange={(e) => handleFileChange('pur', e.target.files[0])}
                        className="hidden"
                      />
                    </label>
                  )}
                </div>

                {/* Invoice Section - View & Edit */}
                {(selectedShipment?.invoice || (invoiceMap[selectedShipment?.awbNumber] || []).length > 0) && (
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                      Invoice
                    </label>
                    <div className="p-3 bg-emerald-50 dark:bg-emerald-900/25 border-2 border-emerald-200 dark:border-emerald-700/50 rounded-lg">
                      <div className="flex items-center gap-2">
                        {selectedShipment?.invoice && (
                          <button
                            type="button"
                            onClick={() => {
                              const pdfUrl = selectedShipment.invoice;
                              const fullUrl = pdfUrl.startsWith('http') ? pdfUrl : getCloudFrontUrl(pdfUrl);
                              const version = selectedShipment.updatedAt ? new Date(selectedShipment.updatedAt).getTime() : Date.now();
                              window.open(`${fullUrl}${fullUrl.includes('?') ? '&' : '?'}v=${version}`, '_blank');
                            }}
                            className="flex-1 px-3 py-2 text-sm font-medium text-emerald-700 dark:text-emerald-300 bg-emerald-100 dark:bg-emerald-800 hover:bg-emerald-200 dark:hover:bg-emerald-700 rounded-md transition flex items-center justify-center gap-2"
                          >
                            <Eye size={18} className="flex-shrink-0" />
                            View Invoice
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => {
                            setShowEditModal(false);
                            const awbInvoices = invoiceMap[selectedShipment?.awbNumber] || [];
                            const existingInvoice = awbInvoices.length > 0 ? awbInvoices[0] : null;
                            if (existingInvoice) {
                              handleEditInvoice(selectedShipment, existingInvoice);
                            } else {
                              handleGenerateInvoice(selectedShipment);
                            }
                          }}
                          className="flex-1 px-3 py-2 text-sm font-medium text-amber-700 dark:text-amber-300 bg-amber-100 dark:bg-amber-800 hover:bg-amber-200 dark:hover:bg-amber-700 rounded-md transition flex items-center justify-center gap-2"
                        >
                          <Pencil size={18} className="flex-shrink-0" />
                          Edit Invoice
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            const inv = (invoiceMap[selectedShipment?.awbNumber] || [])[0];
                            const invoiceId = inv?.id || inv?.invoiceId || inv?.invoice_id;
                            if (invoiceId) {
                              setDeleteInvoiceConfirm({ id: invoiceId, awbNumber: selectedShipment.awbNumber });
                            } else {
                              toast.error('Invoice ID not found');
                            }
                          }}
                          className="px-3 py-2 text-sm font-medium text-red-700 dark:text-red-300 bg-red-100 dark:bg-red-800 hover:bg-red-200 dark:hover:bg-red-700 rounded-md transition flex items-center justify-center gap-2"
                        >
                          <Trash2 size={18} className="flex-shrink-0" />
                          Clear
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 mt-8">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="flex-1 px-6 py-3 bg-slate-200 dark:bg-slate-700 hover:bg-slate-300 dark:hover:bg-slate-600 text-slate-800 dark:text-slate-200 rounded-lg transition font-medium"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition font-medium flex items-center justify-center gap-2"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                  </svg>
                  Upload
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Sea Export Edit Modal for PDF Upload */}
      {showSeaExportEditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-slate-800 rounded-lg max-w-xl w-full overflow-hidden">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-cyan-600 via-teal-600 to-emerald-700 px-6 py-5">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 bg-white/15 backdrop-blur-sm rounded-xl flex items-center justify-center">
                    <Ship size={22} className="text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-white tracking-wide">Sea Export Documents</h3>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="px-2 py-0.5 bg-white/20 text-white text-xs font-semibold rounded-full">
                        B/L {selectedSeaShipment?.blNumber}
                      </span>
                      <span className="px-2 py-0.5 bg-teal-400/30 text-teal-100 text-xs font-medium rounded-full truncate max-w-[160px]">
                        {selectedSeaShipment?.exporterName || 'Sea Export'}
                      </span>
                    </div>
                  </div>
                </div>
                <button
                  onClick={handleCloseSeaExportModal}
                  className="p-1.5 bg-white/10 hover:bg-white/25 rounded-lg text-white transition"
                >
                  <X size={18} />
                </button>
              </div>
            </div>

            <form onSubmit={handleSeaExportUploadPDFs} className="p-6">
              <div className="space-y-6">
                <h4 className="text-lg font-semibold text-slate-900 dark:text-white">Document Uploads</h4>

                {/* Which creditor's bill this PUR is. A PUR always comes from one,
                    and the reader needs to know whose layout to expect. */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                    Creditor {seaEditFiles.pur && <span className="text-rose-500">*</span>}
                  </label>
                  <select
                    value={seaEditCreditorId}
                    onChange={(e) => setSeaEditCreditorId(e.target.value)}
                    className="ql-select w-full px-3 py-2.5 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition"
                  >
                    <option value="">Select the creditor who sent this bill…</option>
                    {creditorList.map((c) => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                  {seaEditFiles.pur && !seaEditCreditorId && (
                    <p className="mt-1.5 text-[11.5px] font-semibold text-amber-600 dark:text-amber-400">
                      Pick the creditor this PUR came from.
                    </p>
                  )}
                </div>

                {/* PUR PDF */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                    PUR PDF
                  </label>
                  {seaEditFiles.pur ? (
                    <div className="flex items-center gap-3 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded-lg">
                      <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      <span className="flex-1 text-sm text-slate-700 dark:text-slate-300 truncate">{seaEditFiles.pur.name}</span>
                      <button type="button" onClick={() => { const fileURL = URL.createObjectURL(seaEditFiles.pur); window.open(fileURL, '_blank'); }} className="text-blue-600 hover:text-blue-800 text-xs font-medium">View</button>
                      <button type="button" onClick={() => openPdfRemoveConfirm({ title: 'Remove PDF?', message: 'Are you sure you want to remove this PUR PDF?', onConfirm: () => setSeaEditFiles(prev => ({ ...prev, pur: null })) })} className="text-red-500 hover:text-red-700">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                      </button>
                    </div>
                  ) : (
                    <label className="flex items-center justify-center gap-2 p-4 border-2 border-dashed border-slate-300 dark:border-slate-600 rounded-lg cursor-pointer hover:border-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/10 transition">
                      <svg className="w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                      </svg>
                      <span className="text-sm text-slate-500 dark:text-slate-400">Click to upload PUR PDF</span>
                      <input type="file" accept=".pdf" onChange={(e) => setSeaEditFiles({...seaEditFiles, pur: e.target.files[0]})} className="hidden" />
                    </label>
                  )}
                </div>

                {/* Invoice Section - View & Edit */}
                {selectedSeaShipment?.invoicePdf && (
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                      Invoice
                    </label>
                    <div className="p-3 bg-emerald-50 dark:bg-emerald-900/25 border-2 border-emerald-200 dark:border-emerald-700/50 rounded-lg">
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => {
                            const pdfUrl = selectedSeaShipment.invoicePdf;
                            const fullUrl = pdfUrl.startsWith('http') ? pdfUrl : getCloudFrontUrl(pdfUrl);
                            const version = selectedSeaShipment.updatedAt ? new Date(selectedSeaShipment.updatedAt).getTime() : Date.now();
                            window.open(`${fullUrl}${fullUrl.includes('?') ? '&' : '?'}v=${version}`, '_blank');
                          }}
                          className="flex-1 px-3 py-2 text-sm font-medium text-emerald-700 dark:text-emerald-300 bg-emerald-100 dark:bg-emerald-800 hover:bg-emerald-200 dark:hover:bg-emerald-700 rounded-md transition flex items-center justify-center gap-2"
                        >
                          <Eye size={18} className="flex-shrink-0" />
                          View Invoice
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setShowSeaExportEditModal(false);
                            const existing = invoiceMap[selectedSeaShipment?.blNumber];
                            if (existing) {
                              handleEditInvoice(selectedSeaShipment, existing);
                            } else {
                              handleGenerateInvoice(selectedSeaShipment);
                            }
                          }}
                          className="flex-1 px-3 py-2 text-sm font-medium text-amber-700 dark:text-amber-300 bg-amber-100 dark:bg-amber-800 hover:bg-amber-200 dark:hover:bg-amber-700 rounded-md transition flex items-center justify-center gap-2"
                        >
                          <Pencil size={18} className="flex-shrink-0" />
                          Edit Invoice
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="flex gap-3 mt-8">
                <button
                  type="button"
                  onClick={handleCloseSeaExportModal}
                  className="flex-1 px-6 py-3 bg-slate-200 dark:bg-slate-700 hover:bg-slate-300 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-200 rounded-lg font-medium transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition flex items-center justify-center gap-2"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                  Upload
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* KYC Upload Modal */}
      {showKycModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-slate-800 rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="sticky top-0 bg-blue-600 px-6 py-4 flex items-center justify-between z-10">
              <div>
                <h3 className="text-2xl font-bold text-white">Upload KYC Documents</h3>
                <p className="text-blue-100 text-sm mt-1">
                  {selectedCustomer ? `Customer: ${selectedCustomer}` : 'Select a customer to upload KYC documents'}
                </p>
              </div>
              <button
                onClick={() => setShowKycModal(false)}
                className="text-white hover:text-slate-200 transition"
              >
                <X size={24} />
              </button>
            </div>
            
            {/* Modal Body */}
            <div className="p-6">
              {/* Customer Selection */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  Select Customer <span className="text-red-500">*</span>
                </label>
                <select
                  value={selectedCustomer}
                  onChange={(e) => setSelectedCustomer(e.target.value)}
                  className="ql-select w-full px-4 py-2.5 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition"
                >
                  <option value="">Select Customer</option>
                  {customers.map((customer, index) => (
                    <option key={`${customer.id}-${customer.name || customer.username || 'customer'}-${index}`} value={customer.name}>
                      {customer.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Success Message */}
              {kycSuccessMessage && (
                <div className="mb-4 p-4 rounded-lg flex items-center gap-3 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-700">
                  <svg className="w-6 h-6 text-green-600 dark:text-green-400 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span className="text-sm font-medium text-green-700 dark:text-green-300">{kycSuccessMessage}</span>
                </div>
              )}

              {/* KYC Documents Grid */}
              <div className="grid grid-cols-1 xs:grid-cols-2 sm:grid-cols-2 md:grid-cols-2 gap-3 sm:gap-4">
                {/* KYC Document */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                    KYC Document
                  </label>
                  {kycFiles.kyc ? (
                    <div className="flex items-center gap-2 p-3 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-700 rounded-lg">
                      <FileText size={18} className="text-green-600 dark:text-green-400 flex-shrink-0" />
                      <button
                        type="button"
                        onClick={() => window.open(URL.createObjectURL(kycFiles.kyc), '_blank')}
                        className="flex-1 text-sm text-green-700 dark:text-green-300 hover:underline text-left truncate"
                      >
                        {kycFiles.kyc.name}
                      </button>
                      <button
                        type="button"
                        onClick={() => openPdfRemoveConfirm({
                          title: 'Remove PDF?',
                          message: 'Are you sure you want to remove this KYC document?',
                          onConfirm: () => handleKycFileChange('kyc', null)
                        })}
                        className="p-1.5 hover:bg-green-200 dark:hover:bg-green-800 rounded transition"
                      >
                        <X size={16} className="text-green-600 dark:text-green-400" />
                      </button>
                    </div>
                  ) : existingKycDocs.kyc ? (
                    <div className="flex items-center gap-2 p-3 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-700 rounded-lg">
                      <FileText size={18} className="text-blue-600 dark:text-blue-400 flex-shrink-0" />
                      <button
                        type="button"
                        onClick={() => window.open(getCloudFrontUrl(existingKycDocs.kyc), '_blank')}
                        className="flex-1 text-sm text-blue-700 dark:text-blue-300 hover:underline text-left truncate"
                      >
                        {existingKycDocs.kyc.split('/').pop()}
                      </button>
                      <button
                        type="button"
                        onClick={() => openPdfRemoveConfirm({
                          title: 'Remove PDF?',
                          message: 'Are you sure you want to remove this KYC document?',
                          onConfirm: () => handleRemoveExistingKyc('kyc')
                        })}
                        className="p-1.5 hover:bg-blue-200 dark:hover:bg-blue-800 rounded transition"
                      >
                        <X size={16} className="text-blue-600 dark:text-blue-400" />
                      </button>
                    </div>
                  ) : (
                    <label className={`flex items-center justify-center gap-2 p-3 border-2 border-dashed rounded-lg transition ${!selectedCustomer ? 'border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 cursor-not-allowed opacity-60' : 'border-slate-300 dark:border-slate-600 cursor-pointer hover:border-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20'}`}>
                      <Upload size={18} className="text-slate-400" />
                      <span className="text-sm text-slate-600 dark:text-slate-400">Upload PDF</span>
                      <input
                        type="file"
                        accept=".pdf"
                        onChange={(e) => !selectedCustomer ? null : handleKycFileChange('kyc', e.target.files[0])}
                        disabled={!selectedCustomer}
                        className="hidden"
                      />
                    </label>
                  )}
                </div>

                {/* Premise Letter */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                    Premise Letter
                  </label>
                  {kycFiles.promiseLetter ? (
                    <div className="flex items-center gap-2 p-3 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-700 rounded-lg">
                      <FileText size={18} className="text-green-600 dark:text-green-400 flex-shrink-0" />
                      <button
                        type="button"
                        onClick={() => window.open(URL.createObjectURL(kycFiles.promiseLetter), '_blank')}
                        className="flex-1 text-sm text-green-700 dark:text-green-300 hover:underline text-left truncate"
                      >
                        {kycFiles.promiseLetter.name}
                      </button>
                      <button
                        type="button"
                        onClick={() => openPdfRemoveConfirm({
                          title: 'Remove PDF?',
                          message: 'Are you sure you want to remove this Premise Letter?',
                          onConfirm: () => handleKycFileChange('promiseLetter', null)
                        })}
                        className="p-1.5 hover:bg-green-200 dark:hover:bg-green-800 rounded transition"
                      >
                        <X size={16} className="text-green-600 dark:text-green-400" />
                      </button>
                    </div>
                  ) : existingKycDocs.promiseLetter ? (
                    <div className="flex items-center gap-2 p-3 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-700 rounded-lg">
                      <FileText size={18} className="text-blue-600 dark:text-blue-400 flex-shrink-0" />
                      <button
                        type="button"
                        onClick={() => window.open(getCloudFrontUrl(existingKycDocs.promiseLetter), '_blank')}
                        className="flex-1 text-sm text-blue-700 dark:text-blue-300 hover:underline text-left truncate"
                      >
                        {existingKycDocs.promiseLetter.split('/').pop()}
                      </button>
                      <button
                        type="button"
                        onClick={() => openPdfRemoveConfirm({
                          title: 'Remove PDF?',
                          message: 'Are you sure you want to remove this Premise Letter?',
                          onConfirm: () => handleRemoveExistingKyc('promiseLetter')
                        })}
                        className="p-1.5 hover:bg-blue-200 dark:hover:bg-blue-800 rounded transition"
                      >
                        <X size={16} className="text-blue-600 dark:text-blue-400" />
                      </button>
                    </div>
                  ) : (
                    <label className={`flex items-center justify-center gap-2 p-3 border-2 border-dashed rounded-lg transition ${!selectedCustomer ? 'border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 cursor-not-allowed opacity-60' : 'border-slate-300 dark:border-slate-600 cursor-pointer hover:border-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20'}`}>
                      <Upload size={18} className="text-slate-400" />
                      <span className="text-sm text-slate-600 dark:text-slate-400">Upload PDF</span>
                      <input
                        type="file"
                        accept=".pdf"
                        onChange={(e) => !selectedCustomer ? null : handleKycFileChange('promiseLetter', e.target.files[0])}
                        disabled={!selectedCustomer}
                        className="hidden"
                      />
                    </label>
                  )}
                </div>

                {/* Bank Letter */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                    Bank Letter
                  </label>
                  {kycFiles.bankLetter ? (
                    <div className="flex items-center gap-2 p-3 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-700 rounded-lg">
                      <FileText size={18} className="text-green-600 dark:text-green-400 flex-shrink-0" />
                      <button
                        type="button"
                        onClick={() => window.open(URL.createObjectURL(kycFiles.bankLetter), '_blank')}
                        className="flex-1 text-sm text-green-700 dark:text-green-300 hover:underline text-left truncate"
                      >
                        {kycFiles.bankLetter.name}
                      </button>
                      <button
                        type="button"
                        onClick={() => openPdfRemoveConfirm({
                          title: 'Remove PDF?',
                          message: 'Are you sure you want to remove this Bank Letter?',
                          onConfirm: () => handleKycFileChange('bankLetter', null)
                        })}
                        className="p-1.5 hover:bg-green-200 dark:hover:bg-green-800 rounded transition"
                      >
                        <X size={16} className="text-green-600 dark:text-green-400" />
                      </button>
                    </div>
                  ) : existingKycDocs.bankLetter ? (
                    <div className="flex items-center gap-2 p-3 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-700 rounded-lg">
                      <FileText size={18} className="text-blue-600 dark:text-blue-400 flex-shrink-0" />
                      <button
                        type="button"
                        onClick={() => window.open(getCloudFrontUrl(existingKycDocs.bankLetter), '_blank')}
                        className="flex-1 text-sm text-blue-700 dark:text-blue-300 hover:underline text-left truncate"
                      >
                        {existingKycDocs.bankLetter.split('/').pop()}
                      </button>
                      <button
                        type="button"
                        onClick={() => openPdfRemoveConfirm({
                          title: 'Remove PDF?',
                          message: 'Are you sure you want to remove this Bank Letter?',
                          onConfirm: () => handleRemoveExistingKyc('bankLetter')
                        })}
                        className="p-1.5 hover:bg-blue-200 dark:hover:bg-blue-800 rounded transition"
                      >
                        <X size={16} className="text-blue-600 dark:text-blue-400" />
                      </button>
                    </div>
                  ) : (
                    <label className={`flex items-center justify-center gap-2 p-3 border-2 border-dashed rounded-lg transition ${!selectedCustomer ? 'border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 cursor-not-allowed opacity-60' : 'border-slate-300 dark:border-slate-600 cursor-pointer hover:border-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20'}`}>
                      <Upload size={18} className="text-slate-400" />
                      <span className="text-sm text-slate-600 dark:text-slate-400">Upload PDF</span>
                      <input
                        type="file"
                        accept=".pdf"
                        onChange={(e) => !selectedCustomer ? null : handleKycFileChange('bankLetter', e.target.files[0])}
                        disabled={!selectedCustomer}
                        className="hidden"
                      />
                    </label>
                  )}
                </div>

                {/* Service Agreement */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                    Service Agreement
                  </label>
                  {kycFiles.serviceAggre ? (
                    <div className="flex items-center gap-2 p-3 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-700 rounded-lg">
                      <FileText size={18} className="text-green-600 dark:text-green-400 flex-shrink-0" />
                      <button
                        type="button"
                        onClick={() => window.open(URL.createObjectURL(kycFiles.serviceAggre), '_blank')}
                        className="flex-1 text-sm text-green-700 dark:text-green-300 hover:underline text-left truncate"
                      >
                        {kycFiles.serviceAggre.name}
                      </button>
                      <button
                        type="button"
                        onClick={() => openPdfRemoveConfirm({
                          title: 'Remove PDF?',
                          message: 'Are you sure you want to remove this Service Agreement?',
                          onConfirm: () => handleKycFileChange('serviceAggre', null)
                        })}
                        className="p-1.5 hover:bg-green-200 dark:hover:bg-green-800 rounded transition"
                      >
                        <X size={16} className="text-green-600 dark:text-green-400" />
                      </button>
                    </div>
                  ) : existingKycDocs.serviceAggre ? (
                    <div className="flex items-center gap-2 p-3 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-700 rounded-lg">
                      <FileText size={18} className="text-blue-600 dark:text-blue-400 flex-shrink-0" />
                      <button
                        type="button"
                        onClick={() => window.open(getCloudFrontUrl(existingKycDocs.serviceAggre), '_blank')}
                        className="flex-1 text-sm text-blue-700 dark:text-blue-300 hover:underline text-left truncate"
                      >
                        {existingKycDocs.serviceAggre.split('/').pop()}
                      </button>
                      <button
                        type="button"
                        onClick={() => openPdfRemoveConfirm({
                          title: 'Remove PDF?',
                          message: 'Are you sure you want to remove this Service Agreement?',
                          onConfirm: () => handleRemoveExistingKyc('serviceAggre')
                        })}
                        className="p-1.5 hover:bg-blue-200 dark:hover:bg-blue-800 rounded transition"
                      >
                        <X size={16} className="text-blue-600 dark:text-blue-400" />
                      </button>
                    </div>
                  ) : (
                    <label className={`flex items-center justify-center gap-2 p-3 border-2 border-dashed rounded-lg transition ${!selectedCustomer ? 'border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 cursor-not-allowed opacity-60' : 'border-slate-300 dark:border-slate-600 cursor-pointer hover:border-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20'}`}>
                      <Upload size={18} className="text-slate-400" />
                      <span className="text-sm text-slate-600 dark:text-slate-400">Upload PDF</span>
                      <input
                        type="file"
                        accept=".pdf"
                        onChange={(e) => !selectedCustomer ? null : handleKycFileChange('serviceAggre', e.target.files[0])}
                        disabled={!selectedCustomer}
                        className="hidden"
                      />
                    </label>
                  )}
                </div>
              </div>

              {/* Action Buttons */}
              {selectedCustomer && (kycFiles.kyc || kycFiles.promiseLetter || kycFiles.bankLetter || kycFiles.serviceAggre) && (
                <div className="flex gap-3 mt-6 pt-6 border-t border-slate-200 dark:border-slate-700">
                  <button
                    type="button"
                    onClick={() => {
                      setShowKycModal(false);
                      setKycFiles({ kyc: null, promiseLetter: null, bankLetter: null, serviceAggre: null });
                    }}
                    className="flex-1 px-6 py-3 bg-slate-200 dark:bg-slate-700 hover:bg-slate-300 dark:hover:bg-slate-600 text-slate-800 dark:text-slate-200 rounded-lg transition font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={async () => {
                      await handleUploadKyc();
                      if (kycSuccessMessage) {
                        setTimeout(() => {
                          setShowKycModal(false);
                        }, 2000);
                      }
                    }}
                    className="flex-1 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition font-medium flex items-center justify-center gap-2"
                  >
                    <Upload size={20} />
                    Upload KYC Documents
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {pdfRemoveConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[90] p-4">
          <div className="bg-white dark:bg-slate-800 rounded-lg shadow-2xl w-full max-w-md">
            <div className="p-6">
              <div className="flex items-center justify-center mb-4">
                <div className="w-14 h-14 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center">
                  <AlertTriangle className="text-red-600 dark:text-red-400" size={28} />
                </div>
              </div>
              <h3 className="text-lg font-semibold text-slate-900 dark:text-white text-center">
                {pdfRemoveConfirm.title || 'Remove PDF?'}
              </h3>
              <p className="mt-2 text-sm text-slate-600 dark:text-slate-300 text-center">
                {pdfRemoveConfirm.message || 'Are you sure you want to remove this PDF?'}
              </p>
              <div className="flex gap-3 mt-6">
                <button
                  type="button"
                  onClick={closePdfRemoveConfirm}
                  className="flex-1 px-4 py-2.5 bg-slate-200 dark:bg-slate-700 hover:bg-slate-300 dark:hover:bg-slate-600 text-slate-800 dark:text-slate-200 rounded-lg transition font-medium"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={confirmPdfRemove}
                  className="flex-1 px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-lg transition font-medium"
                >
                  Yes, Remove
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Invoice Confirmation Modal */}
      {paymentUpdate && (
        <UpdatePaymentModal
          shipmentNo={paymentUpdate.shipmentNo}
          invoice={paymentUpdate.invoice}
          onClose={() => setPaymentUpdate(null)}
          onUpdated={() => fetchAllInvoices()}
        />
      )}

      {deleteInvoiceConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100] p-4">
          <div className="bg-white dark:bg-slate-800 rounded-lg shadow-2xl w-full max-w-md">
            <div className="p-6">
              <div className="flex items-center justify-center mb-4">
                <div className="w-14 h-14 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center">
                  <Trash2 className="text-red-600 dark:text-red-400" size={28} />
                </div>
              </div>
              <h3 className="text-lg font-semibold text-slate-900 dark:text-white text-center">
                Clear Invoice?
              </h3>
              <p className="mt-2 text-sm text-slate-600 dark:text-slate-300 text-center">
                Are you sure you want to delete the invoice for AWB <span className="font-medium">{deleteInvoiceConfirm.awbNumber}</span>? This action cannot be undone.
              </p>
              <div className="flex gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => setDeleteInvoiceConfirm(null)}
                  disabled={deletingInvoice}
                  className="flex-1 px-4 py-2.5 bg-slate-200 dark:bg-slate-700 hover:bg-slate-300 dark:hover:bg-slate-600 text-slate-800 dark:text-slate-200 rounded-lg transition font-medium"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleDeleteInvoice}
                  disabled={deletingInvoice}
                  className="flex-1 px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-lg transition font-medium flex items-center justify-center gap-2"
                >
                  {deletingInvoice ? (
                    <>
                      <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                      Deleting...
                    </>
                  ) : (
                    'Yes, Delete'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showDatePicker && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
          onClick={() => setShowDatePicker(false)}>
          <div className="rounded-2xl shadow-2xl overflow-hidden max-w-fit max-h-[90vh] flex flex-col
                          bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700"
            onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between gap-6 px-5 py-3.5 border-b border-slate-100 dark:border-slate-800">
              <div>
                <p className="text-[14px] font-bold text-slate-900 dark:text-white">Shipment dates</p>
                <p className="text-[11.5px] text-slate-500 dark:text-slate-400">
                  Pick a day for that day alone, or a start and an end for a range.
                </p>
              </div>
              <button type="button" onClick={() => setShowDatePicker(false)}
                className="p-2 rounded-lg text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800">
                <X size={18} />
              </button>
            </div>

            <div className="ql-daterange overflow-auto flex-1">
              <DateRangePicker
                ranges={dateRange || [{ startDate: new Date(), endDate: new Date(), key: 'selection' }]}
                onChange={(item) => setDateRange([item.selection])}
                staticRanges={DATE_PRESETS}
                inputRanges={[]}
                months={window.innerWidth < 768 ? 1 : 2}
                direction={window.innerWidth < 768 ? 'vertical' : 'horizontal'}
                showSelectionPreview
                moveRangeOnFirstSelection={false}
                minDate={new Date(2020, 0, 1)}
                maxDate={new Date()}
                rangeColors={['#2563eb']}
              />
            </div>

            <div className="flex items-center justify-between gap-3 px-5 py-3.5 border-t border-slate-100 dark:border-slate-800">
              <button type="button" className={btn.quiet}
                onClick={() => { setDateRange(null); setShowDatePicker(false); }}>
                Clear dates
              </button>
              <button type="button" className={btn.primary} onClick={() => setShowDatePicker(false)}>
                Apply
              </button>
            </div>
          </div>
        </div>
      )}

      <CustomerInvoicesModal
        open={customerInvoiceListOpen}
        onClose={() => setCustomerInvoiceListOpen(false)}
        onEdit={(invoice) => { setCustomerInvoiceEdit(invoice); setCustomerInvoiceOpen(true); }}
      />
    </AdminShell>
  );
};

export default AccountantDashboard;
